import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import { History, ChevronRight, Loader2, Trash2, Plus, ArrowLeft, Send, FileDown, CreditCard, PenTool, Link2, Copy, Package, Banknote, Mail, FileSignature, Building2, ArrowRightLeft, Inbox } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtTs = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
};
const fmtPct = (n) => (typeof n === "number" ? `${n.toFixed(3)}%` : "—");

const STATUS_COLORS = {
  open: "bg-[#E6EFEC] text-[#0B4636]",
  closing: "bg-[#FFF4DB] text-[#8B5E00]",
  closed: "bg-[#E5E5DF] text-[#0A0A0A]",
  matured: "bg-[#102968] text-white",
};

const KIND_COLORS = {
  lifecycle: "border-l-[#0B4636] bg-[#FAFAF6]",
  treasury_delta: "border-l-[#102968] bg-[#F0F4FA]",
};

const PATH_LABEL = {
  spv_trust: "SPV Trust Setup",
  discount_sale: "Outright Sale",
  financing: "Collateral Loan",
};

/**
 * Slide-out drawer that exposes the new /api/transactions/{id}/events and
 * /replay endpoints. Power users can:
 *   (a) "Record this deal" — POST current deal to /api/transactions
 *   (b) Browse all recorded transactions, see status pill + path
 *   (c) Drill into one transaction → see merged timeline + benchmark drift
 *   (d) Advance lifecycle (sale_executed, maturity_paid, etc.) which
 *       triggers the webhook fan-out and persists to db.event_log
 */
export default function AuditTrailDrawer({ deal, mode, open: controlledOpen, onOpenChange, intent, focusTxId }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = (v) => { if (onOpenChange) onOpenChange(v); else setInternalOpen(v); };
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // tx_id
  const [replay, setReplay] = useState(null);
  const [creating, setCreating] = useState(false);
  const [advancing, setAdvancing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [downloadingBinder, setDownloadingBinder] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signatures, setSignatures] = useState([]);
  const [signerForm, setSignerForm] = useState({ name: "", email: "", role: "Buyer" });
  const [paymentStatus, setPaymentStatus] = useState(null);

  const refreshList = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/transactions`);
      setList(r.data || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { if (open) refreshList(); }, [open]);

  const loadReplay = async (txId) => {
    setSelected(txId);
    setReplay(null);
    try {
      const r = await axios.get(`${API}/transactions/${txId}/replay`);
      setReplay(r.data);
    } catch (e) {
      setReplay({ error: e.message });
    }
  };

  const createTx = async () => {
    setCreating(true);
    try {
      const payload = {
        path: mode,
        trust_name: deal.trustName || deal.borrowerName,
        counterparty_name:
          mode === "discount_sale" ? deal.buyerName
            : mode === "financing" ? deal.lenderName
            : (deal.originatorName || deal.borrowerName),
        face_value: Number(deal.faceValue) || 0,
        purchase_price: Number(deal.purchasePrice) || 0,
        coupon_rate: Number(deal.couponRate) || 0,
        term_days: Number(deal.termDays) || 0,
        execution_date: deal.executionDate || new Date().toISOString().slice(0, 10),
        maturity_date: deal.maturityDate || new Date().toISOString().slice(0, 10),
      };
      const r = await axios.post(`${API}/transactions`, payload);
      await refreshList();
      loadReplay(r.data.id);
    } catch (e) { /* ignore */ }
    setCreating(false);
  };

  const advance = async (txId, type) => {
    setAdvancing(type);
    try {
      await axios.post(`${API}/transactions/${txId}/events`, { type, data: {} });
      await loadReplay(txId);
      await refreshList();
    } catch (e) { /* ignore */ }
    setAdvancing(null);
  };

  const deleteTx = async (txId) => {
    if (!window.confirm("Delete this transaction record? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/transactions/${txId}`);
      setSelected(null);
      setReplay(null);
      await refreshList();
    } catch (e) { /* ignore */ }
  };

  const downloadBinder = async (txId) => {
    // Use the /binder/signed pipeline (React + Chrome) so the downloaded
    // closing binder always carries cursive /s/ signatures, the round
    // notary seal, the chain-of-title block, and a SHA-256 hash. The
    // backend falls back to the tx's stored deal_snapshot + merges user
    // profile defaults if the live deal payload is missing fields.
    setDownloadingBinder(true);
    try {
      // Operate on the SELECTED transaction's own data, never the workbench
      // deal. Send the tx's own path and an empty deal so the backend builds
      // the binder from the authoritative stored deal_snapshot + profile.
      const txObj = list.find((x) => x.id === txId);
      const txPath = txObj?.path || mode;
      const r = await axios.post(
        `${API}/transactions/${txId}/binder/signed`,
        { path: txPath, deal: {} },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      const safe = (txObj?.trust_name || txObj?.counterparty_name || "deal").replace(/\s+/g, "_");
      a.download = `${safe}_closing_binder.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const sha = r.headers["x-th-binder-sha256"];
      if (sha) {
        const el = document.createElement("div");
        el.textContent = `✓ Binder downloaded · SHA-256 ${sha.slice(0, 12)}…`;
        Object.assign(el.style, { position: "fixed", bottom: "20px", left: "50%", transform: "translateX(-50%)",
          background: "#0B4636", color: "#fff", padding: "10px 18px", borderRadius: "4px",
          fontSize: "12px", letterSpacing: "0.1em", zIndex: 9999, fontFamily: "monospace" });
        document.body.appendChild(el);
        setTimeout(() => el.remove(), 3500);
      }
    } catch (e) {
      window.alert("Binder download failed: " + (e.response?.data?.detail || e.message));
    }
    setDownloadingBinder(false);
  };

  const downloadSignedBinder = async (txId) => {
    setDownloadingBinder(true);
    try {
      const txObj = list.find((x) => x.id === txId);
      const txPath = txObj?.path || mode;
      const r = await axios.post(
        `${API}/transactions/${txId}/binder/signed`,
        { path: txPath, deal: {} },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      const safe = (txObj?.trust_name || txObj?.counterparty_name || "deal").replace(/\s+/g, "_");
      a.download = `${safe}_signed_binder.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      // Surface the SHA-256 hash returned in the response headers.
      const sha = r.headers["x-th-binder-sha256"];
      if (sha) window.alert(`Signed Closing Binder downloaded.\n\nSHA-256: ${sha}\n\nThe hash is also persisted on the transaction record.`);
    } catch (e) { /* ignore */ }
    setDownloadingBinder(false);
  };

  const submitToCounterparty = async (txId) => {
    const url = window.prompt(
      "Counterparty webhook URL (where the signed binder + wire instructions will be POSTed):",
      "https://httpbin.org/post"
    );
    if (!url) return;
    const secret = window.prompt(
      "HMAC shared secret (optional — adds X-TH-Signature header):",
      ""
    );
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const txPath = list.find((x) => x.id === txId)?.path || mode;
      const r = await axios.post(`${API}/transactions/${txId}/submit`, {
        path: txPath,
        deal: {},
        counterparty_url: url,
        counterparty_secret: secret || null,
      });
      setSubmitResult(r.data);
      await loadReplay(txId);
      await refreshList();
    } catch (e) {
      setSubmitResult({ error: e.message });
    }
    setSubmitting(false);
  };

  // ---------- Stripe checkout (Buyer pays the discount price) ----------
  const payWithStripe = async (txId) => {
    setStripeLoading(true);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        transaction_id: txId,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe Checkout
      window.location.href = r.data.url;
    } catch (e) {
      window.alert("Stripe checkout failed: " + (e.response?.data?.detail || e.message));
      setStripeLoading(false);
    }
  };

  // Poll Stripe payment status when redirected back with session_id
  const pollPayment = async (sessionId, attempts = 0) => {
    if (attempts >= 6) {
      setPaymentStatus({ payment_status: "timeout" });
      return;
    }
    try {
      const r = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
      setPaymentStatus(r.data);
      if (r.data.payment_status === "paid" || r.data.status === "expired") {
        await loadReplay(selected);
        await refreshList();
        return;
      }
      setTimeout(() => pollPayment(sessionId, attempts + 1), 2000);
    } catch (e) {
      setPaymentStatus({ error: e.message });
    }
  };

  // On first mount, auto-detect ?stripe_session_id=... in the URL and start polling.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("stripe_session_id");
    const tx = params.get("tx");
    if (sid && tx) {
      setOpen(true);
      loadReplay(tx);
      pollPayment(sid);
    }
    // eslint-disable-next-line
  }, []);

  // ---------- Click-to-Sign ----------
  const loadSignatures = async (txId) => {
    try {
      const r = await axios.get(`${API}/transactions/${txId}/signatures`);
      setSignatures(r.data.signatures || []);
    } catch (e) { /* ignore */ }
  };

  const sign = async (txId) => {
    if (!signerForm.name || !signerForm.email) {
      window.alert("Please enter signer name and email.");
      return;
    }
    setSigning(true);
    try {
      const r = await axios.post(`${API}/transactions/${txId}/sign`, {
        signer_name: signerForm.name,
        signer_email: signerForm.email,
        signer_role: signerForm.role,
        attestation: "I have reviewed the Closing Binder and attest to its accuracy.",
      });
      setSignatures((prev) => [...prev, r.data]);
      setSignerForm({ name: "", email: "", role: signerForm.role });
      await loadReplay(txId);
      await refreshList();
    } catch (e) {
      window.alert("Sign failed: " + (e.response?.data?.detail || e.message));
    }
    setSigning(false);
  };

  // When a tx is selected, load its signatures
  useEffect(() => {
    if (selected) loadSignatures(selected);
    else setSignatures([]);
  }, [selected]);

  // ---------- Counterparty share-link ----------
  const [shareLink, setShareLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);

  const generateShareLink = async (txId) => {
    setGeneratingLink(true);
    try {
      const r = await axios.post(`${API}/transactions/${txId}/share-link`, {
        expires_in_days: 30,
        audience: "counterparty",
      });
      setShareLink(r.data);
      await loadReplay(txId);
    } catch (e) {
      window.alert("Could not generate share link: " + (e.response?.data?.detail || e.message));
    }
    setGeneratingLink(false);
  };

  const copyShareLink = () => {
    if (!shareLink?.share_url) return;
    navigator.clipboard?.writeText(shareLink.share_url);
    window.alert("Share link copied to clipboard.");
  };

  // Reset share link when selection changes
  useEffect(() => { setShareLink(null); setTransmissions([]); if (selected) loadTransmissions(selected); }, [selected]);

  // ---------- Bank Package ZIP (CashPro / Access / CEO / email / SFTP) ----------
  const [pkgLoading, setPkgLoading] = useState(false);
  const [pkgTarget, setPkgTarget] = useState("cashpro");

  // ---------- Iteration 19 settlement integrations ----------
  // NACHA
  const [nachaLoading, setNachaLoading] = useState(false);
  const [nachaForm, setNachaForm] = useState({
    seller_routing: "", seller_account: "", seller_name: "",
    immediate_destination: "", immediate_origin: "", origin_dfi_id: "", company_id: "",
  });
  // Resend email
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailResult, setEmailResult] = useState(null);
  // Dropbox Sign
  const [dsLoading, setDsLoading] = useState(false);
  const [dsForm, setDsForm] = useState({ signer_email: "", signer_name: "", test_mode: true });
  const [dsResult, setDsResult] = useState(null);
  // Increase
  const [incLoading, setIncLoading] = useState(false);
  const [incAccounts, setIncAccounts] = useState(null); // null = not fetched, [] = none, [...] = list
  const [incForm, setIncForm] = useState({
    rail: "ach", routing_number: "", account_number: "", beneficiary_name: "", account_id: "",
  });
  const [incResult, setIncResult] = useState(null);

  const fetchIncreaseAccounts = async () => {
    try {
      const r = await axios.get(`${API}/integrations/increase/accounts`);
      setIncAccounts(r.data.accounts || []);
      if ((r.data.accounts || []).length && !incForm.account_id) {
        setIncForm((f) => ({ ...f, account_id: r.data.accounts[0].id }));
      }
    } catch (e) {
      setIncAccounts({ error: e.response?.data?.detail || e.message });
    }
  };

  // Auto-fetch Increase accounts when a tx is selected
  useEffect(() => {
    if (selected && incAccounts === null) fetchIncreaseAccounts();
    // eslint-disable-next-line
  }, [selected]);

  const generateNacha = async (txId) => {
    if (!nachaForm.seller_routing || !nachaForm.seller_account || !nachaForm.seller_name) {
      window.alert("Enter seller routing, account, and name to generate the NACHA file.");
      return;
    }
    setNachaLoading(true);
    try {
      const r = await axios.post(`${API}/transactions/${txId}/nacha`, nachaForm, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `nacha_${txId.slice(0, 8)}.txt`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      const sha = r.headers["x-th-nacha-sha256"];
      if (sha) window.alert(`NACHA file generated.\n\nSHA-256: ${sha}\n\nUpload this .txt file via your bank's corporate ACH portal (CashPro, Access, etc.).`);
      await loadReplay(txId);
    } catch (e) {
      window.alert("NACHA generation failed: " + (e.response?.data?.detail || e.message));
    }
    setNachaLoading(false);
  };

  const emailBinder = async (txId) => {
    if (!emailTo) { window.alert("Enter the recipient email."); return; }
    setEmailLoading(true); setEmailResult(null);
    try {
      const r = await axios.post(`${API}/transactions/${txId}/email-binder`, {
        path: mode, deal, to_email: emailTo,
      });
      setEmailResult(r.data);
      await loadReplay(txId);
    } catch (e) {
      setEmailResult({ error: e.response?.data?.detail || e.message });
    }
    setEmailLoading(false);
  };

  const sendDropboxSign = async (txId) => {
    if (!dsForm.signer_email || !dsForm.signer_name) {
      window.alert("Enter signer name and email."); return;
    }
    setDsLoading(true); setDsResult(null);
    try {
      const r = await axios.post(`${API}/transactions/${txId}/dropbox-sign`, {
        path: mode, deal, ...dsForm,
      });
      setDsResult(r.data);
      await loadReplay(txId);
    } catch (e) {
      setDsResult({ error: e.response?.data?.detail || e.message });
    }
    setDsLoading(false);
  };

  // ---------- Iteration 22 — Inbound ACH (receive payments INTO Increase) ----------
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositInfo, setDepositInfo] = useState(null);

  const fetchDepositInstructions = async () => {
    if (!incForm.account_id) {
      window.alert("Select an Increase source account first.");
      return;
    }
    setDepositLoading(true); setDepositInfo(null);
    try {
      const r = await axios.get(`${API}/integrations/increase/account/${incForm.account_id}/details`);
      setDepositInfo(r.data);
    } catch (e) {
      setDepositInfo({ error: e.response?.data?.detail || e.message });
    }
    setDepositLoading(false);
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard?.writeText(text).then(() => {
      // brief toast
      const el = document.createElement("div");
      el.textContent = `✓ ${label} copied`;
      Object.assign(el.style, { position: "fixed", bottom: "20px", left: "50%", transform: "translateX(-50%)",
        background: "#0B4636", color: "#fff", padding: "8px 16px", borderRadius: "4px",
        fontSize: "12px", letterSpacing: "0.1em", zIndex: 9999 });
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 1500);
    });
  };

  // ---------- Iteration 22 — Counterparty Library ----------
  const [cps, setCps] = useState([]);
  const [showSaveCp, setShowSaveCp] = useState(false);
  const [cpForm, setCpForm] = useState({
    name: "", role: "buyer", legal_name: "", contact_email: "",
    contact_name: "", routing_number: "", account_number: "", bank_name: "",
  });

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/counterparties`).then(r => setCps(r.data.counterparties || [])).catch(() => {});
  }, [open]);

  const saveCounterparty = async () => {
    if (!cpForm.name.trim()) { window.alert("Profile name is required."); return; }
    try {
      await axios.post(`${API}/counterparties`, cpForm);
      const r = await axios.get(`${API}/counterparties`);
      setCps(r.data.counterparties || []);
      setShowSaveCp(false);
      setCpForm({ name: "", role: "buyer", legal_name: "", contact_email: "", contact_name: "", routing_number: "", account_number: "", bank_name: "" });
    } catch (e) {
      window.alert("Save failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const applyCounterparty = (cp) => {
    setIncForm(f => ({
      ...f,
      beneficiary_name: cp.legal_name || cp.name,
      routing_number: cp.routing_number || f.routing_number,
      account_number: cp.account_number || f.account_number,
    }));
    if (cp.contact_email) setEmailTo(cp.contact_email);
    if (cp.contact_email) setDsForm(f => ({ ...f, signer_email: cp.contact_email, signer_name: cp.contact_name || cp.name }));
  };

  const initiateIncreaseTransfer = async (txId) => {
    if (!incForm.routing_number || !incForm.account_number || !incForm.beneficiary_name) {
      window.alert("Enter beneficiary name, routing number, and account number."); return;
    }
    if (!incForm.account_id) {
      window.alert("Pick an Increase source account first. If none appear, create one at increase.com → Accounts.");
      return;
    }
    if (!window.confirm(`This will initiate a real ${incForm.rail.toUpperCase()} from Increase production for the discount price. Continue?`)) return;
    setIncLoading(true); setIncResult(null);
    try {
      const r = await axios.post(`${API}/transactions/${txId}/increase/transfer`, incForm);
      setIncResult(r.data);
      await loadReplay(txId);
    } catch (e) {
      setIncResult({ error: e.response?.data?.detail || e.message });
    }
    setIncLoading(false);
  };

  // ---------- Iteration 25 — Direct Exchange (bypass Increase) ----------
  // Records counterparty bank wire instructions on the tx and tracks the
  // settlement out-of-band. Funds move directly between the buyer's bank and
  // Treasury House's bank (or vice versa). No Increase API key required.
  const [dxLoading, setDxLoading] = useState(false);
  const [dxResult, setDxResult] = useState(null);
  const [dxForm, setDxForm] = useState({
    direction: "buyer_to_seller",
    rail: "wire",
    counterparty_bank_name: "",
    counterparty_routing: "",
    counterparty_account: "",
    counterparty_swift: "",
    expected_amount: "",
    expected_settle_date: "",
    notes: "",
  });
  const [markPaidForm, setMarkPaidForm] = useState({ paid_amount: "", reference_number: "", notes: "" });

  // ---------- Direct-to-Creditor (debt settlement / swap / collateral) ----------
  const [debtTemplates, setDebtTemplates] = useState([]);
  const [crLoading, setCrLoading] = useState(false);
  const [crDeliverLoading, setCrDeliverLoading] = useState(false);
  const [crResult, setCrResult] = useState(null);
  const [secureWebhookUrl, setSecureWebhookUrl] = useState("");
  const [authHeaderName, setAuthHeaderName] = useState("Authorization");
  const [authHeaderValue, setAuthHeaderValue] = useState("");
  const [deliveryChannel, setDeliveryChannel] = useState("webhook");
  const [sftp, setSftp] = useState({ host: "", port: 22, username: "", password: "", private_key: "", key_passphrase: "", target_folder: "" });
  const [cpDeliveryName, setCpDeliveryName] = useState("");

  const applyCounterpartyDelivery = (cp) => {
    if (!cp) return;
    if (cp.delivery_channel === "sftp" || (cp.sftp_host && !cp.webhook_url)) {
      setDeliveryChannel("sftp");
      setSftp({ host: cp.sftp_host || "", port: cp.sftp_port || 22, username: cp.sftp_username || "", password: cp.sftp_password || "", private_key: cp.sftp_private_key || "", key_passphrase: cp.sftp_key_passphrase || "", target_folder: cp.sftp_target_folder || "" });
    } else {
      setDeliveryChannel("webhook");
      setSecureWebhookUrl(cp.webhook_url || "");
      setAuthHeaderName(cp.auth_header_name || "Authorization");
      setAuthHeaderValue(cp.auth_header_value || "");
    }
    setCpDeliveryName(cp.name || "");
  };

  const saveDeliveryToCounterparty = async () => {
    const name = (cpDeliveryName || "").trim();
    if (!name) { setTransmitResult({ error: "Enter a name to save these delivery details under." }); return; }
    const existing = cps.find((c) => (c.name || "").toLowerCase() === name.toLowerCase());
    const payload = {
      ...(existing || {}), name, role: existing?.role || "bank", delivery_channel: deliveryChannel,
      webhook_url: deliveryChannel === "webhook" ? secureWebhookUrl : (existing?.webhook_url || null),
      auth_header_name: deliveryChannel === "webhook" ? authHeaderName : (existing?.auth_header_name || null),
      auth_header_value: deliveryChannel === "webhook" ? authHeaderValue : (existing?.auth_header_value || null),
      sftp_host: deliveryChannel === "sftp" ? sftp.host : (existing?.sftp_host || null),
      sftp_port: deliveryChannel === "sftp" ? (Number(sftp.port) || 22) : (existing?.sftp_port || 22),
      sftp_username: deliveryChannel === "sftp" ? sftp.username : (existing?.sftp_username || null),
      sftp_password: deliveryChannel === "sftp" ? sftp.password : (existing?.sftp_password || null),
      sftp_private_key: deliveryChannel === "sftp" ? sftp.private_key : (existing?.sftp_private_key || null),
      sftp_key_passphrase: deliveryChannel === "sftp" ? sftp.key_passphrase : (existing?.sftp_key_passphrase || null),
      sftp_target_folder: deliveryChannel === "sftp" ? sftp.target_folder : (existing?.sftp_target_folder || null),
    };
    try {
      await axios.post(`${API}/counterparties`, payload);
      const r = await axios.get(`${API}/counterparties`); setCps(r.data.counterparties || []);
      setTransmitResult({ ok: true, transmitted: false, saved: true });
    } catch (e) {
      setTransmitResult({ error: e.response?.data?.detail || e.message });
    }
  };
  const [transmitLoading, setTransmitLoading] = useState(false);
  const [transmitResult, setTransmitResult] = useState(null);
  const [transmissions, setTransmissions] = useState([]);

  const loadTransmissions = async (txId) => {
    if (!txId) return;
    try {
      const r = await axios.get(`${API}/transactions/${txId}/transmissions`);
      setTransmissions(r.data.transmissions || []);
    } catch { setTransmissions([]); }
  };
  const [binderLoading, setBinderLoading] = useState(false);
  const [crForm, setCrForm] = useState({
    creditor_name: "", creditor_address: "", creditor_email: "",
    purpose: "settle_obligation", debt_type: "mortgage_sf",
    account_number: "", current_balance: "", payoff_amount: "", servicer: "",
    effective_date: "", collateral_description: "",
    borrower_name: "", borrower_address: "", borrower_ssn: "", routing_number: "",
  });

  useEffect(() => {
    axios.get(`${API}/creditor/debt-templates`)
      .then((r) => setDebtTemplates(r.data.templates || []))
      .catch(() => { /* noop */ });
  }, []);

  const applyDebtTemplate = (id) => {
    const t = debtTemplates.find((x) => x.id === id);
    if (!t) return;
    setCrForm((f) => ({ ...f, debt_type: t.debtType, current_balance: t.currentBalance, payoff_amount: t.payoffAmount }));
  };

  const submitCreditorDirection = async (txId) => {
    if (!crForm.creditor_name.trim()) { alert("Creditor / lender name is required."); return; }
    setCrLoading(true); setCrResult(null);
    try {
      const payload = {
        ...crForm,
        current_balance: crForm.current_balance ? Number(crForm.current_balance) : null,
        payoff_amount: crForm.payoff_amount ? Number(crForm.payoff_amount) : null,
      };
      const r = await axios.post(`${API}/transactions/${txId}/creditor-direction`, payload);
      setCrResult({ ok: true, instruments: r.data.direction?.instruments || [] });
      await loadReplay(txId);
    } catch (e) {
      setCrResult({ error: e.response?.data?.detail || e.message });
    }
    setCrLoading(false);
  };

  const deliverToCreditor = async (txId) => {
    setCrDeliverLoading(true);
    try {
      const r = await axios.post(`${API}/transactions/${txId}/creditor/deliver`, {});
      setCrResult({ ok: true, delivered: true, to: r.data.to_email });
    } catch (e) {
      setCrResult({ error: e.response?.data?.detail || e.message });
    }
    setCrDeliverLoading(false);
  };

  const transmitSecurePayload = async (txId) => {
    const url = secureWebhookUrl.trim();
    if (!url) { setTransmitResult({ error: "Enter the counterparty's webhook URL." }); return; }
    if (!/^https?:\/\/.+\..+/.test(url)) {
      setTransmitResult({ error: `Invalid webhook URL — must start with https:// and include a domain (got '${url.slice(0, 50)}'). Check for typos like 'htpps'.` });
      return;
    }
    setTransmitLoading(true); setTransmitResult(null);
    try {
      const r = await axios.post(`${API}/transactions/${txId}/transmit-secure-payload`, {
        webhook_url: url,
        auth_header_name: authHeaderValue ? (authHeaderName || "Authorization") : undefined,
        auth_header_value: authHeaderValue || undefined,
      });
      if (r.data.ok) setTransmitResult({ ok: true, transmitted: true, hash: r.data.hash, status_code: r.data.status_code });
      else setTransmitResult({ error: r.data.detail || "Transmission failed." });
    } catch (e) {
      setTransmitResult({ error: e.response?.data?.detail || e.message });
    }
    setTransmitLoading(false);
    loadTransmissions(txId);
  };

  const transmitToInbox = async (txId) => {
    // One-click: transmit the HMAC-signed payload to THIS app's own hosted
    // Acceptance Inbox (no counterparty registration / external infra needed).
    setTransmitLoading(true); setTransmitResult(null);
    try {
      const r = await axios.post(`${API}/transactions/${txId}/transmit-secure-payload`, {
        webhook_url: `${API}/inbound/payloads`,
      });
      if (r.data.ok) setTransmitResult({ ok: true, transmitted: true, inbox: true, hash: r.data.hash, status_code: r.data.status_code });
      else setTransmitResult({ error: r.data.detail || "Transmission failed." });
    } catch (e) {
      setTransmitResult({ error: e.response?.data?.detail || e.message });
    }
    setTransmitLoading(false);
    loadTransmissions(txId);
  };

  const transmitViaSftp = async (txId) => {
    if (!sftp.host.trim() || !sftp.username.trim()) { setTransmitResult({ error: "SFTP host and username are required." }); return; }
    if (!sftp.password && !sftp.private_key) { setTransmitResult({ error: "Provide an SSH private key or a password." }); return; }
    setTransmitLoading(true); setTransmitResult(null);
    try {
      const r = await axios.post(`${API}/transactions/${txId}/transmit-sftp`, {
        host: sftp.host.trim(), port: Number(sftp.port) || 22, username: sftp.username.trim(),
        password: sftp.password || undefined, private_key: sftp.private_key || undefined,
        key_passphrase: sftp.key_passphrase || undefined, target_folder: sftp.target_folder || undefined,
      });
      if (r.data.ok) setTransmitResult({ ok: true, transmitted: true, hash: r.data.hash, sftp: true, uploaded: r.data.uploaded });
      else setTransmitResult({ error: r.data.detail || "SFTP delivery failed." });
    } catch (e) {
      setTransmitResult({ error: e.response?.data?.detail || e.message });
    }
    setTransmitLoading(false);
    loadTransmissions(txId);
  };

  const openBinder = async (txId) => {
    setBinderLoading(true);
    try {
      const r = await axios.post(`${API}/transactions/${txId}/share-link`, { expires_in_days: 30 });
      window.open(`${window.location.origin}/print/${txId}?t=${r.data.token}`, "_blank");
    } catch (e) {
      window.alert("Could not open binder: " + (e.response?.data?.detail || e.message));
    }
    setBinderLoading(false);
  };

  // Creditor-intent: when opened via the "Direct to Creditor" button, auto-record
  // the current working deal so the user lands straight on the creditor form.
  const creditorAutoRan = useRef(false);
  useEffect(() => {
    if (!open) {
      creditorAutoRan.current = false;
      setSelected(null);
      setReplay(null);
      return;
    }
    if (intent === "creditor" && !selected && !creating && !creditorAutoRan.current) {
      creditorAutoRan.current = true;
      createTx();
    }
    // eslint-disable-next-line
  }, [open, intent, selected, creating]);

  // Payload-intent: deep-link (?deliver=<txId>) auto-selects the deal and scrolls
  // to the Secure Payload Delivery panel.
  const payloadAutoRan = useRef(false);
  useEffect(() => {
    if (!open) { payloadAutoRan.current = false; return; }
    if (intent === "payload" && focusTxId && !payloadAutoRan.current) {
      payloadAutoRan.current = true;
      loadReplay(focusTxId);
      setTimeout(() => {
        const el = document.querySelector('[data-testid="secure-payload-block"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 900);
    }
    // eslint-disable-next-line
  }, [open, intent, focusTxId]);

  const openReceipt = (txId, t) => {
    const d = t.data || {};
    const tx = currentTx || {};
    const esc = (s) => String(s ?? "").replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));
    const fmtAmt = d.face_value != null ? `$${Number(d.face_value).toLocaleString()}` : (tx.face_value != null ? `$${Number(tx.face_value).toLocaleString()}` : "—");
    const ts = t.timestamp ? new Date(t.timestamp).toLocaleString() : "—";
    const rows = [
      ["Status", d.ok ? `Delivered · HTTP ${d.status_code ?? ""}` : `Failed${d.status_code ? ` · HTTP ${d.status_code}` : ""}`],
      ["Transmitted At", ts],
      ["Collateral / Deal ID", txId],
      ["Trust / Seller", tx.trust_name || "—"],
      ["Counterparty", tx.counterparty_name || "—"],
      ["Transaction Type", d.transaction_type || "—"],
      ["Face Value", fmtAmt],
      ["Borrower / Payee", d.borrower_name || "—"],
      ["Borrower SSN (masked)", d.borrower_ssn_masked || "—"],
      ["Deposit Account #", d.deposit_account_number || "—"],
      ["Routing # (ABA)", d.routing_number || "—"],
      ["Transport Security", d.mtls ? "Mutual TLS (client cert) + HMAC-SHA256" : "TLS + HMAC-SHA256"],
      ["Binder Fidelity", (d.binder_fidelity === "full_render" ? "Full render" : "Summary fallback") + (d.binder_bytes ? ` (${Math.round(d.binder_bytes / 1024)} KB)` : "")],
      ["Counterparty Webhook", d.webhook_url || "—"],
      ["SHA-256 Integrity Hash", d.hash || "—"],
    ];
    const tableRows = rows.map(([k, v]) =>
      `<tr><td class="k">${esc(k)}</td><td class="v">${esc(v)}</td></tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Transmission Receipt · ${esc(txId)}</title>
      <style>
        *{box-sizing:border-box} body{font-family:Georgia,'Times New Roman',serif;color:#1a1a1a;margin:0;padding:40px;background:#f4f4ef}
        .sheet{max-width:720px;margin:0 auto;background:#fff;border:1px solid #d8d8cf;padding:44px 48px}
        .top{border-bottom:2px solid #0B6B4F;padding-bottom:14px;margin-bottom:8px}
        .brand{font-size:12px;letter-spacing:.22em;text-transform:uppercase;color:#0B6B4F;font-weight:bold}
        .title{font-size:24px;margin:6px 0 2px}
        .sub{font-size:12px;color:#666}
        .badge{display:inline-block;margin-top:10px;padding:4px 12px;border-radius:3px;font-size:12px;font-weight:bold;letter-spacing:.08em;text-transform:uppercase}
        .ok{background:#0B6B4F;color:#fff}.fail{background:#b91c1c;color:#fff}
        table{width:100%;border-collapse:collapse;margin-top:18px}
        td{padding:8px 6px;border-bottom:1px solid #ececec;font-size:13px;vertical-align:top}
        td.k{color:#666;width:42%;text-transform:uppercase;letter-spacing:.06em;font-size:11px}
        td.v{font-family:'Courier New',monospace;word-break:break-all}
        .note{margin-top:22px;font-size:11px;color:#888;line-height:1.5;border-top:1px solid #ececec;padding-top:14px}
        .actions{max-width:720px;margin:18px auto 0;text-align:right}
        button,a.btn{font-family:Arial;font-size:13px;padding:9px 18px;border-radius:4px;border:none;cursor:pointer;text-decoration:none;margin-left:8px}
        .print{background:#0B6B4F;color:#fff}.dl{background:#102968;color:#fff}
        @media print{.actions{display:none}body{background:#fff;padding:0}.sheet{border:none}}
      </style></head><body>
      <div class="sheet">
        <div class="top">
          <div class="brand">SEUTRADE FINANCIAL SERVICES · Secure Payload Delivery</div>
          <div class="title">Transmission Receipt</div>
          <div class="sub">Encrypted Data Exchange · HMAC-SHA256 Signed Webhook</div>
          <div class="badge ${d.ok ? "ok" : "fail"}">${d.ok ? "Transmitted &amp; Delivered" : "Delivery Failed"}</div>
        </div>
        <table>${tableRows}</table>
        <div class="note">This receipt evidences that the encrypted collateral binder was uploaded to secure object storage (AES-256 at rest), a 10-minute presigned link was minted, and a payload signed with HMAC-SHA256 was transmitted to the counterparty's webhook endpoint over TLS. The SHA-256 hash above is the integrity fingerprint of the delivered binder. Sensitive identifiers (SSN) are masked.</div>
      </div>
      <div class="actions">
        <button class="print" onclick="window.print()">Print / Save as PDF</button>
      </div>
    </body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
  };

  const downloadReceiptPdf = async (txId, t) => {
    try {
      const r = await axios.get(`${API}/transactions/${txId}/transmissions/${encodeURIComponent(t.id || "")}/receipt.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `transmission-receipt-${txId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      window.alert("Could not download receipt: " + (e.response?.data?.detail || e.message));
    }
  };

  const renderSecurePayloadBlock = (txId) => (
    <div className="mt-2 p-2 rounded-sm border border-[#0B6B4F]/40 bg-[#0B6B4F]/5" data-testid="secure-payload-block">
      <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#0B6B4F] font-bold mb-1.5">Secure Payload Delivery · Encrypted Data Exchange</div>
      {cps.filter((c) => c.delivery_channel || c.webhook_url || c.sftp_host).length > 0 && (
        <select defaultValue="" data-testid="load-counterparty-delivery"
          onChange={(e) => { const cp = cps.find((c) => c.id === e.target.value); applyCounterpartyDelivery(cp); e.target.value = ""; }}
          className="w-full border border-[#0B6B4F]/40 px-2 py-1.5 rounded-sm text-[10px] mb-2 bg-white">
          <option value="">— load saved counterparty delivery details —</option>
          {cps.filter((c) => c.delivery_channel || c.webhook_url || c.sftp_host).map((c) => (
            <option key={c.id} value={c.id}>{c.name} ({c.delivery_channel || (c.sftp_host ? "sftp" : "webhook")})</option>
          ))}
        </select>
      )}
      <div className="flex gap-1 mb-2" data-testid="delivery-channel-toggle">
        <button onClick={() => setDeliveryChannel("webhook")} data-testid="channel-webhook-btn"
          className={`flex-1 text-[9px] uppercase tracking-[0.1em] py-1 rounded-sm border ${deliveryChannel === "webhook" ? "bg-[#0B6B4F] text-white border-[#0B6B4F]" : "bg-white text-[#0B6B4F] border-[#0B6B4F]/40"}`}>
          Webhook / API
        </button>
        <button onClick={() => setDeliveryChannel("sftp")} data-testid="channel-sftp-btn"
          className={`flex-1 text-[9px] uppercase tracking-[0.1em] py-1 rounded-sm border ${deliveryChannel === "sftp" ? "bg-[#0B6B4F] text-white border-[#0B6B4F]" : "bg-white text-[#0B6B4F] border-[#0B6B4F]/40"}`}>
          SFTP (banks / servicers)
        </button>
      </div>

      {deliveryChannel === "webhook" ? (
        <>
          <input type="url" placeholder="Counterparty webhook URL (https://…/receive)" value={secureWebhookUrl}
            onChange={(e) => setSecureWebhookUrl(e.target.value)} data-testid="secure-webhook-url"
            className="w-full border border-[#0B6B4F]/40 px-2 py-1.5 rounded-sm text-[11px] mb-1.5" />
          <details className="mb-1.5">
            <summary className="text-[9px] uppercase tracking-[0.12em] text-[#0B6B4F] cursor-pointer select-none" data-testid="secure-auth-toggle">
              Counterparty auth (optional — for endpoints that require an API key / token)
            </summary>
            <div className="grid grid-cols-3 gap-1.5 mt-1.5">
              <input type="text" placeholder="Header (e.g. Authorization)" value={authHeaderName}
                onChange={(e) => setAuthHeaderName(e.target.value)} data-testid="secure-auth-header-name"
                className="col-span-1 border border-[#0B6B4F]/30 px-2 py-1.5 rounded-sm text-[10px]" />
              <input type="text" placeholder="Value (e.g. Bearer abc123 or API key)" value={authHeaderValue}
                onChange={(e) => setAuthHeaderValue(e.target.value)} data-testid="secure-auth-header-value"
                className="col-span-2 border border-[#0B6B4F]/30 px-2 py-1.5 rounded-sm text-[10px] font-mono-fin" />
            </div>
          </details>
          <button onClick={() => transmitSecurePayload(txId)} disabled={transmitLoading}
            className="w-full bg-[#0B6B4F] hover:bg-[#0d7d5c] text-white text-[10.5px] uppercase tracking-[0.14em] py-1.5 rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
            data-testid="cr-transmit-payload-btn">
            {transmitLoading ? "Transmitting…" : "Transmit Secure Payload"}
          </button>
          <button onClick={() => transmitToInbox(txId)} disabled={transmitLoading}
            className="w-full mt-1.5 border border-[#0B6B4F] text-[#0B6B4F] hover:bg-[#0B6B4F]/10 text-[10px] uppercase tracking-[0.12em] py-1.5 rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
            data-testid="cr-transmit-inbox-btn">
            <Inbox size={11} /> {transmitLoading ? "Sending…" : "Send to my Acceptance Inbox (no registration)"}
          </button>
          <div className="text-[9px] text-[#5C5C5C] mt-1">Uploads the encrypted binder to S3, mints a 10-min presigned link, and POSTs an HMAC-signed payload (with SHA-256 integrity hash + borrower identity, masked SSN, and account/routing) to the counterparty. <span className="text-[#0B6B4F]">Acceptance Inbox</span> records it in your own app and returns a signed acceptance receipt — no counterparty setup required.</div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-1.5 mb-1.5">
            <input type="text" placeholder="SFTP host (sftp.lender.com)" value={sftp.host}
              onChange={(e) => setSftp(s => ({ ...s, host: e.target.value }))} data-testid="sftp-host"
              className="col-span-3 border border-[#0B6B4F]/40 px-2 py-1.5 rounded-sm text-[11px]" />
            <input type="number" placeholder="Port" value={sftp.port}
              onChange={(e) => setSftp(s => ({ ...s, port: e.target.value }))} data-testid="sftp-port"
              className="col-span-1 border border-[#0B6B4F]/40 px-2 py-1.5 rounded-sm text-[11px]" />
          </div>
          <div className="grid grid-cols-2 gap-1.5 mb-1.5">
            <input type="text" placeholder="Username" value={sftp.username}
              onChange={(e) => setSftp(s => ({ ...s, username: e.target.value }))} data-testid="sftp-username"
              className="border border-[#0B6B4F]/40 px-2 py-1.5 rounded-sm text-[11px]" />
            <input type="text" placeholder="Target folder (/inbound)" value={sftp.target_folder}
              onChange={(e) => setSftp(s => ({ ...s, target_folder: e.target.value }))} data-testid="sftp-folder"
              className="border border-[#0B6B4F]/40 px-2 py-1.5 rounded-sm text-[11px]" />
          </div>
          <input type="password" placeholder="Password (if no SSH key)" value={sftp.password}
            onChange={(e) => setSftp(s => ({ ...s, password: e.target.value }))} data-testid="sftp-password"
            className="w-full border border-[#0B6B4F]/40 px-2 py-1.5 rounded-sm text-[11px] mb-1.5" />
          <details className="mb-1.5">
            <summary className="text-[9px] uppercase tracking-[0.12em] text-[#0B6B4F] cursor-pointer select-none" data-testid="sftp-key-toggle">
              Use SSH private key instead of password (recommended)
            </summary>
            <textarea placeholder="-----BEGIN OPENSSH PRIVATE KEY----- …" value={sftp.private_key}
              onChange={(e) => setSftp(s => ({ ...s, private_key: e.target.value }))} data-testid="sftp-private-key"
              rows={3} className="w-full border border-[#0B6B4F]/30 px-2 py-1.5 rounded-sm text-[9px] font-mono-fin mt-1.5" />
            <input type="password" placeholder="Key passphrase (if any)" value={sftp.key_passphrase}
              onChange={(e) => setSftp(s => ({ ...s, key_passphrase: e.target.value }))} data-testid="sftp-key-passphrase"
              className="w-full border border-[#0B6B4F]/30 px-2 py-1.5 rounded-sm text-[10px] mt-1" />
          </details>
          <button onClick={() => transmitViaSftp(txId)} disabled={transmitLoading}
            className="w-full bg-[#0B6B4F] hover:bg-[#0d7d5c] text-white text-[10.5px] uppercase tracking-[0.14em] py-1.5 rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
            data-testid="sftp-transmit-btn">
            {transmitLoading ? "Uploading…" : "Deliver Binder via SFTP"}
          </button>
          <div className="text-[9px] text-[#5C5C5C] mt-1">Pushes the binder PDF + a JSON manifest (asset_type=TIME_DRAFT, masked SSN, account/routing, SHA-256) to the counterparty's SFTP intake folder. The bank/servicer provides these connection details.</div>
        </>
      )}

      <div className="flex gap-1.5 mt-2 pt-2 border-t border-[#0B6B4F]/20">
        <input placeholder="Save these details as… (counterparty name)" value={cpDeliveryName}
          onChange={(e) => setCpDeliveryName(e.target.value)} data-testid="save-delivery-name"
          className="flex-1 border border-[#0B6B4F]/30 px-2 py-1 rounded-sm text-[10px]" />
        <button onClick={saveDeliveryToCounterparty} data-testid="save-delivery-btn"
          className="bg-[#102968] hover:bg-[#0a1f50] text-white text-[9.5px] uppercase tracking-[0.1em] px-3 py-1 rounded-sm">
          Save
        </button>
      </div>

      {transmitResult && (
        <div className={`mt-1.5 p-1.5 rounded-sm text-[10px] ${transmitResult.error ? "bg-red-50 text-red-800 border border-red-200" : "bg-[#0B6B4F]/10 text-[#0B6B4F] border border-[#0B6B4F]/30"}`} data-testid="secure-payload-result">
          {transmitResult.error ? (<>Error: {transmitResult.error}</>)
            : transmitResult.saved ? (<div className="font-bold">✓ Delivery details saved to counterparty library</div>)
            : (<><div className="font-bold">✓ {transmitResult.sftp ? "Binder delivered via SFTP" : "Secure payload transmitted"}{transmitResult.status_code ? ` · HTTP ${transmitResult.status_code}` : ""}</div><div className="text-[9.5px] mt-0.5 font-mono break-all">SHA-256: {transmitResult.hash?.slice(0, 32)}…</div></>)}
        </div>
      )}

      {transmissions.length > 0 && (
        <div className="mt-2 pt-2 border-t border-[#0B6B4F]/30" data-testid="transmission-log">
          <div className="text-[9px] uppercase tracking-[0.12em] text-[#0B6B4F] font-bold mb-1">Transmission Log ({transmissions.length})</div>
          <div className="space-y-1 max-h-52 overflow-y-auto">
            {transmissions.map((t, i) => (
              <div key={t.id || i} className="text-[9.5px] bg-white/60 border border-[#0B6B4F]/20 rounded-sm px-1.5 py-1" data-testid={`transmission-log-row-${i}`}>
                <div className="flex items-center justify-between">
                  <span className={t.data?.ok ? "text-[#0B6B4F] font-bold" : "text-red-700 font-bold"}>
                    {t.data?.ok ? "✓ Delivered" : "✗ Failed"} {t.data?.status_code ? `· HTTP ${t.data.status_code}` : ""}
                  </span>
                  <span className="text-[#5C5C5C]">{t.timestamp ? new Date(t.timestamp).toLocaleString() : ""}</span>
                </div>
                <div className="text-[#5C5C5C] truncate">→ {t.data?.webhook_url}</div>
                {t.data?.borrower_name && <div className="text-[#5C5C5C] truncate">Borrower: {t.data.borrower_name}{t.data?.borrower_ssn_masked ? ` · SSN ${t.data.borrower_ssn_masked}` : ""}</div>}
                {(t.data?.deposit_account_number || t.data?.routing_number) && (
                  <div className="text-[#5C5C5C] truncate font-mono">Acct {t.data?.deposit_account_number || "—"} · ABA {t.data?.routing_number || "—"}</div>
                )}
                <div className="text-[#5C5C5C] font-mono truncate">SHA-256: {(t.data?.hash || "").slice(0, 40)}…</div>
                <div className="text-[8.5px] uppercase tracking-[0.1em] text-[#0B6B4F]/80 mt-0.5">
                  {t.data?.channel === "sftp" ? "SFTP · SSH (key/password)" : (t.data?.mtls ? "🔒 mTLS + HMAC-SHA256" : "TLS + HMAC-SHA256")}
                </div>
                {t.data?.binder_fidelity && (
                  <span className={`inline-block mt-0.5 text-[8.5px] uppercase tracking-[0.08em] font-bold px-1 py-0.5 rounded-sm ${t.data.binder_fidelity === "full_render" ? "bg-[#0B6B4F]/10 text-[#0B6B4F]" : "bg-amber-100 text-amber-800"}`}>
                    {t.data.binder_fidelity === "full_render" ? "Full render" : "Summary fallback"}
                    {t.data?.binder_bytes ? ` · ${Math.round(t.data.binder_bytes / 1024)} KB` : ""}
                  </span>
                )}
                <div className="flex gap-2 mt-1">
                  <button onClick={() => openReceipt(txId, t)} data-testid={`transmission-receipt-${i}`}
                    className="text-[9px] uppercase tracking-[0.1em] text-[#0B6B4F] underline hover:text-[#0d7d5c]">
                    View / Print Receipt
                  </button>
                  <button onClick={() => downloadReceiptPdf(txId, t)} data-testid={`transmission-receipt-pdf-${i}`}
                    className="text-[9px] uppercase tracking-[0.1em] text-[#102968] underline hover:text-[#0c1f50]">
                    Download PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderCreditorPanel = () => (
    <div className="border-l-4 border-[#8B5E00] pl-3" data-testid="integration-direct-creditor">
      <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8B5E00] flex items-center gap-1.5 mb-1.5">
        <Building2 size={12} /> Direct to Creditor · Debt Resolution
      </div>
      <div className="text-[10.5px] text-[#5C5C5C] mb-2">
        Sell, transfer or tender this note package to a lender/creditor to settle, swap, finance, refinance or
        collateralize an existing debt obligation. Generates the matching instruments into the binder, then
        delivers a secure link to the creditor.
      </div>

      <div className="grid grid-cols-2 gap-1.5 mb-1.5">
        <div>
          <label className="text-[9.5px] uppercase tracking-[0.12em] text-[#5C5C5C] block mb-0.5">Purpose</label>
          <select value={crForm.purpose} onChange={(e) => setCrForm(f => ({ ...f, purpose: e.target.value }))}
            className="w-full text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white" data-testid="cr-purpose">
            <option value="settle_obligation">Settle Existing Obligation</option>
            <option value="debt_settlement">Debt Settlement</option>
            <option value="debt_swap">Debt Swap</option>
            <option value="collateral">Pledge as Collateral</option>
            <option value="finance">Finance</option>
            <option value="refinance">Refinance</option>
          </select>
        </div>
        <div>
          <label className="text-[9.5px] uppercase tracking-[0.12em] text-[#5C5C5C] block mb-0.5">Pre-loaded debt</label>
          <select onChange={(e) => applyDebtTemplate(e.target.value)} defaultValue=""
            className="w-full text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white" data-testid="cr-template">
            <option value="">— select / TBD —</option>
            {debtTemplates.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-1.5 mb-1.5">
        <input type="text" placeholder="Creditor / lender name" value={crForm.creditor_name}
          onChange={(e) => setCrForm(f => ({ ...f, creditor_name: e.target.value }))}
          className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white" data-testid="cr-name" />
        <input type="email" placeholder="Creditor email (for delivery)" value={crForm.creditor_email}
          onChange={(e) => setCrForm(f => ({ ...f, creditor_email: e.target.value }))}
          className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white" data-testid="cr-email" />
      </div>
      <input type="text" placeholder="Creditor address (optional)" value={crForm.creditor_address}
        onChange={(e) => setCrForm(f => ({ ...f, creditor_address: e.target.value }))}
        className="w-full text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white mb-1.5" data-testid="cr-address" />

      <div className="grid grid-cols-2 gap-1.5 mb-1.5">
        <div>
          <label className="text-[9.5px] uppercase tracking-[0.12em] text-[#5C5C5C] block mb-0.5">Debt type</label>
          <select value={crForm.debt_type} onChange={(e) => setCrForm(f => ({ ...f, debt_type: e.target.value }))}
            className="w-full text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white" data-testid="cr-debt-type">
            <option value="mortgage_sf">Mortgage — Single Family</option>
            <option value="mortgage_mf">Mortgage — Multifamily</option>
            <option value="mortgage_commercial">Mortgage — Commercial</option>
            <option value="auto">Auto Loan</option>
            <option value="student">Student Loan</option>
            <option value="loc">Line of Credit</option>
            <option value="credit_card">Credit Card</option>
            <option value="other">Other</option>
          </select>
        </div>
        <input type="text" placeholder="Account / loan #" value={crForm.account_number}
          onChange={(e) => setCrForm(f => ({ ...f, account_number: e.target.value }))}
          className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white self-end font-mono-fin" data-testid="cr-account" />
      </div>

      <div className="grid grid-cols-2 gap-1.5 mb-1.5">
        <input type="number" placeholder="Current balance ($)" value={crForm.current_balance}
          onChange={(e) => setCrForm(f => ({ ...f, current_balance: e.target.value }))}
          className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white font-mono-fin" data-testid="cr-balance" />
        <input type="number" placeholder="Payoff / settle amount ($)" value={crForm.payoff_amount}
          onChange={(e) => setCrForm(f => ({ ...f, payoff_amount: e.target.value }))}
          className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white font-mono-fin" data-testid="cr-payoff" />
      </div>
      <div className="grid grid-cols-2 gap-1.5 mb-2">
        <input type="text" placeholder="Servicer (optional)" value={crForm.servicer}
          onChange={(e) => setCrForm(f => ({ ...f, servicer: e.target.value }))}
          className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white" data-testid="cr-servicer" />
        <input type="date" value={crForm.effective_date}
          onChange={(e) => setCrForm(f => ({ ...f, effective_date: e.target.value }))}
          className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white" data-testid="cr-effective-date" />
      </div>

      <div className="text-[9px] uppercase tracking-[0.12em] text-[#8A8A8A] mt-1">Borrower / Payee — pledged/assigned in the transmittal</div>
      <input type="text" placeholder="Borrower / payee full name" value={crForm.borrower_name}
        onChange={(e) => setCrForm(f => ({ ...f, borrower_name: e.target.value }))}
        className="w-full text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white mt-1" data-testid="cr-borrower-name" />
      <div className="grid grid-cols-2 gap-1.5 mt-1.5">
        <input type="text" placeholder="Borrower address" value={crForm.borrower_address}
          onChange={(e) => setCrForm(f => ({ ...f, borrower_address: e.target.value }))}
          className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white" data-testid="cr-borrower-address" />
        <input type="text" placeholder="Borrower SSN / TIN" value={crForm.borrower_ssn}
          onChange={(e) => setCrForm(f => ({ ...f, borrower_ssn: e.target.value }))}
          className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white" data-testid="cr-borrower-ssn" />
      </div>
      <div className="text-[9px] uppercase tracking-[0.12em] text-[#8A8A8A] mt-1.5">Deposit instructions — sent in the transmittal (account # above)</div>
      <input type="text" placeholder="Routing # (9-digit ABA) for the pledged/deposit account" value={crForm.routing_number}
        onChange={(e) => setCrForm(f => ({ ...f, routing_number: e.target.value }))}
        className="w-full text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white mt-1 font-mono-fin" data-testid="cr-routing" />

      <div className="flex gap-1.5">
        <button onClick={() => submitCreditorDirection(selected)} disabled={crLoading}
          className="flex-1 bg-[#8B5E00] hover:bg-[#714c00] text-white text-[10.5px] uppercase tracking-[0.14em] py-1.5 rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
          data-testid="cr-generate-btn">
          <Building2 size={11} /> {crLoading ? "Generating…" : "Generate Instruments"}
        </button>
        <button onClick={() => deliverToCreditor(selected)} disabled={crDeliverLoading}
          className="flex-1 border border-[#8B5E00] text-[#8B5E00] hover:bg-[#8B5E00]/10 text-[10.5px] uppercase tracking-[0.14em] py-1.5 rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
          data-testid="cr-deliver-btn">
          {crDeliverLoading ? "Sending…" : "Deliver to Creditor"}
        </button>
      </div>

      <div className="flex gap-1.5 mt-1.5">
        <button onClick={() => openBinder(selected)} disabled={binderLoading}
          className="flex-1 bg-[#102968] hover:bg-[#0c1f50] text-white text-[10.5px] uppercase tracking-[0.14em] py-1.5 rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
          data-testid="cr-view-binder-btn">
          <FileDown size={11} /> {binderLoading ? "Opening…" : "View Binder & Documents"}
        </button>
        <button onClick={() => downloadBinder(selected)} disabled={downloadingBinder}
          className="flex-1 border border-[#102968] text-[#102968] hover:bg-[#102968]/10 text-[10.5px] uppercase tracking-[0.14em] py-1.5 rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
          data-testid="cr-download-binder-btn">
          {downloadingBinder ? "…" : "Download PDF"}
        </button>
      </div>

      {renderSecurePayloadBlock(selected)}

      {crResult && (
        <div className={`mt-2 p-2 rounded-sm text-[10.5px] ${crResult.error ? "bg-red-50 text-red-800 border border-red-200" : "bg-[#8B5E00]/10 text-[#8B5E00] border border-[#8B5E00]/30"}`} data-testid="cr-result">
          {crResult.error ? (<>Error: {crResult.error}</>)
            : crResult.transmitted ? (<><div className="font-bold">✓ Secure payload transmitted</div><div className="text-[10px] mt-0.5 font-mono break-all">SHA-256: {crResult.hash?.slice(0, 32)}…</div></>)
            : crResult.delivered ? (<><div className="font-bold">✓ Secure package link sent</div><div className="text-[10px] mt-0.5">to {crResult.to}</div></>)
            : (<><div className="font-bold">✓ Instruments generated into binder</div><div className="text-[10px] mt-0.5">{(crResult.instruments || []).length} document(s) added · open the binder to review.</div></>)}
        </div>
      )}
    </div>
  );

  const initiateDirectExchange = async (txId) => {
    if (!dxForm.counterparty_bank_name && !dxForm.counterparty_routing && !dxForm.counterparty_account) {
      if (!window.confirm("No counterparty bank details entered. Record direct-exchange as pending anyway?")) return;
    }
    setDxLoading(true); setDxResult(null);
    try {
      const payload = {
        ...dxForm,
        expected_amount: dxForm.expected_amount ? Number(dxForm.expected_amount) : undefined,
      };
      const r = await axios.post(`${API}/transactions/${txId}/settlement/direct-exchange`, payload);
      setDxResult(r.data);
      await loadReplay(txId);
    } catch (e) {
      setDxResult({ error: e.response?.data?.detail || e.message });
    }
    setDxLoading(false);
  };

  const markSettlementPaid = async (txId) => {
    if (!window.confirm("Mark this settlement as paid? This closes the deal and finalizes the binder.")) return;
    setDxLoading(true);
    try {
      const payload = {
        paid_amount: markPaidForm.paid_amount ? Number(markPaidForm.paid_amount) : undefined,
        reference_number: markPaidForm.reference_number || undefined,
        notes: markPaidForm.notes || undefined,
      };
      const r = await axios.post(`${API}/transactions/${txId}/settlement/mark-paid`, payload);
      setDxResult({ ...r.data, _paid: true });
      await loadReplay(txId);
    } catch (e) {
      setDxResult({ error: e.response?.data?.detail || e.message });
    }
    setDxLoading(false);
  };

  const downloadBankPackage = async (txId) => {
    setPkgLoading(true);
    try {
      const r = await axios.post(
        `${API}/transactions/${txId}/bank-package`,
        { path: mode, deal, bank_target: pkgTarget },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      const safe = (deal.trustName || deal.borrowerName || "deal").replace(/\s+/g, "_");
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `${safe}_BA_${pkgTarget}_${date}.zip`;
      document.body.appendChild(a);
      a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      const sha = r.headers["x-th-package-sha256"];
      const count = r.headers["x-th-file-count"];
      if (sha) window.alert(`Bank Package ZIP downloaded.\n\nFiles: ${count}\nSHA-256: ${sha}\n\nUpload this ZIP to your bank's portal — instructions are in the cover letter.`);
      await loadReplay(txId);
    } catch (e) {
      window.alert("Bank package generation failed: " + (e.response?.data?.detail || e.message));
    }
    setPkgLoading(false);
  };

  const currentTx = selected ? list.find((x) => x.id === selected) : null;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="btn-ghost" data-testid="audit-trail-open-btn" title="Open the transaction audit trail">
          <History size={14} />
          Audit Trail
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[640px] p-0 bg-[#FAFAF6] overflow-y-auto"
        data-testid="audit-trail-drawer"
      >
        <div className="sticky top-0 z-10 bg-white border-b border-[#E5E5DF] px-5 py-4 flex items-center justify-between">
          <SheetHeader className="p-0">
            <SheetTitle className="font-serif-display text-[20px] leading-tight">
              {intent === "creditor" ? "Direct to Creditor" : selected ? "Transaction Audit Trail" : "Recorded Transactions"}
            </SheetTitle>
            <SheetDescription className="text-[11px] text-[#5C5C5C] tracking-[0.14em] uppercase mt-0.5">
              {intent === "creditor" ? "Debt settlement · swap · finance · collateral"
                : selected ? `Replay · ${selected.slice(0, 8)}…` : `${list.length} on record · MongoDB-backed`}
            </SheetDescription>
          </SheetHeader>
          {selected && intent !== "creditor" ? (
            <button
              className="btn-ghost"
              onClick={() => { setSelected(null); setReplay(null); }}
              data-testid="audit-trail-back-btn"
            >
              <ArrowLeft size={14} /> Back
            </button>
          ) : null}
        </div>

        {/* ============= LIST VIEW ============= */}
        {!selected && intent !== "creditor" && (
          <div className="p-5 space-y-3" data-testid="audit-trail-list-view">
            <button
              onClick={createTx}
              disabled={creating}
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-[#0B4636] text-[#0B4636] hover:bg-[#E6EFEC] rounded-sm py-3 text-[13px] font-medium uppercase tracking-[0.1em] transition"
              data-testid="audit-trail-record-btn"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Record this deal as a transaction
            </button>

            {loading && (
              <div className="text-center py-8 text-[#8A8A8A] text-[12px]">
                <Loader2 size={16} className="animate-spin inline mr-2" />
                Loading…
              </div>
            )}

            {!loading && list.length === 0 && (
              <div className="text-center py-10 text-[12px] text-[#8A8A8A]" data-testid="audit-trail-empty">
                No transactions on record yet. Click <em>&ldquo;Record this deal&rdquo;</em> above to start an audit trail tied to the live U.S. Treasury benchmark.
              </div>
            )}

            {list.map((tx) => {
              const txmits = (tx.events || []).filter((e) => e.type === "secure_payload_transmitted");
              const lastTx = txmits[txmits.length - 1];
              return (
              <button
                key={tx.id}
                onClick={() => loadReplay(tx.id)}
                className="w-full text-left border border-[#E5E5DF] bg-white hover:border-[#0B4636] rounded-sm px-4 py-3 transition"
                data-testid={`audit-trail-item-${tx.id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[13.5px] truncate">{tx.trust_name}</div>
                    <div className="text-[10.5px] font-mono-fin text-[#8A8A8A] uppercase tracking-[0.12em] mt-0.5">
                      {PATH_LABEL[tx.path] || tx.path} · ${Number(tx.face_value).toLocaleString()} · {fmtTs(tx.created_at)}
                    </div>
                    <div className="text-[11px] text-[#5C5C5C] mt-1 truncate">↔ {tx.counterparty_name}</div>
                    {lastTx ? (
                      <div
                        className={`inline-flex items-center gap-1 mt-1.5 text-[9.5px] uppercase tracking-[0.1em] font-bold px-1.5 py-0.5 rounded-sm ${lastTx.data?.ok ? "bg-[#0B6B4F]/10 text-[#0B6B4F] border border-[#0B6B4F]/30" : "bg-red-50 text-red-700 border border-red-200"}`}
                        data-testid={`transmission-status-${tx.id}`}
                      >
                        <Send size={9} />
                        {lastTx.data?.ok ? "Binder Transmitted" : "Transmit Failed"}
                        {txmits.length > 1 ? ` ×${txmits.length}` : ""}
                        {lastTx.data?.borrower_ssn_masked ? ` · SSN ${lastTx.data.borrower_ssn_masked}` : ""}
                        <span className="opacity-70 font-normal normal-case">· {fmtTs(lastTx.timestamp)}</span>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-1 mt-1.5 text-[9.5px] uppercase tracking-[0.1em] text-[#A8A8A0] border border-[#E5E5DF] px-1.5 py-0.5 rounded-sm" data-testid={`transmission-status-${tx.id}`}>
                        Not Transmitted
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span
                      className={`text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-0.5 rounded-sm ${STATUS_COLORS[tx.status] || ""}`}
                      data-testid={`audit-trail-status-${tx.id}`}
                    >
                      {tx.status}
                    </span>
                    <ChevronRight size={14} className="text-[#8A8A8A]" />
                  </div>
                </div>
              </button>
              );
            })}
          </div>
        )}

        {/* ============= DETAIL VIEW ============= */}
        {/* ============= DIRECT-TO-CREDITOR FOCUSED VIEW ============= */}
        {intent === "creditor" && (
          <div className="p-5 space-y-4" data-testid="direct-creditor-focused-view">
            {!selected ? (
              <div className="text-center py-10 text-[12px] text-[#8A8A8A]" data-testid="creditor-preparing">
                <Loader2 size={16} className="animate-spin inline mr-2" />
                Recording this deal &amp; preparing the creditor package…
              </div>
            ) : (
              <>
                {currentTx && (
                  <div className="bg-white border border-[#E5E5DF] rounded-sm p-4">
                    <div className="font-serif-display text-[15.5px] leading-tight">{currentTx.trust_name}</div>
                    <div className="text-[10.5px] font-mono-fin text-[#8A8A8A] uppercase tracking-[0.12em] mt-1">
                      {PATH_LABEL[currentTx.path] || currentTx.path} · ${Number(currentTx.face_value).toLocaleString()}
                    </div>
                    <div className="text-[11px] text-[#5C5C5C] mt-1">Directing this package to a lender/creditor.</div>
                  </div>
                )}
                {renderCreditorPanel()}
              </>
            )}
          </div>
        )}

        {/* ============= DETAIL VIEW ============= */}
        {selected && intent !== "creditor" && (
          <div className="p-5 space-y-4" data-testid="audit-trail-detail-view">
            {/* Header card */}
            {currentTx && (
              <div className="bg-white border border-[#E5E5DF] rounded-sm p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-serif-display text-[15.5px] leading-tight">{currentTx.trust_name}</div>
                    <div className="text-[10.5px] font-mono-fin text-[#8A8A8A] uppercase tracking-[0.12em] mt-1">
                      {PATH_LABEL[currentTx.path] || currentTx.path}
                    </div>
                    <div className="text-[12px] text-[#5C5C5C] mt-1">↔ {currentTx.counterparty_name}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-0.5 rounded-sm ${STATUS_COLORS[currentTx.status] || ""}`}>
                    {currentTx.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3 text-[11px]">
                  <div><span className="text-[#8A8A8A]">Face:</span> <span className="font-mono-fin">${Number(currentTx.face_value).toLocaleString()}</span></div>
                  <div><span className="text-[#8A8A8A]">Sale:</span> <span className="font-mono-fin">${Number(currentTx.purchase_price).toLocaleString()}</span></div>
                  <div><span className="text-[#8A8A8A]">Coupon:</span> <span className="font-mono-fin">{Number(currentTx.coupon_rate).toFixed(2)}%</span></div>
                  <div><span className="text-[#8A8A8A]">Term:</span> <span className="font-mono-fin">{currentTx.term_days}d</span></div>
                </div>
              </div>
            )}

            {/* Settlement + Submit actions */}
            {currentTx && (
              <div className="bg-gradient-to-br from-[#102968] to-[#0B4636] text-white rounded-sm p-4" data-testid="audit-trail-submit-panel">
                <div className="text-[10px] uppercase tracking-[0.16em] opacity-80 mb-1">Execution &amp; Settlement</div>
                <div className="text-[12px] mb-3 opacity-90 leading-snug">
                  Generate the Closing Binder PDF (with full audit trail) and submit it to the counterparty over webhook + HMAC. Records a <span className="font-mono">submitted_to_counterparty</span> lifecycle event.
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => downloadBinder(selected)}
                    disabled={downloadingBinder}
                    className="bg-white text-[#102968] hover:bg-[#F0F4FA] disabled:opacity-50 px-3 py-1.5 text-[11.5px] uppercase tracking-[0.1em] font-bold rounded-sm flex items-center gap-1"
                    data-testid="audit-trail-binder-btn"
                  >
                    {downloadingBinder ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
                    Download Closing Binder PDF
                  </button>
                  <button
                    onClick={() => submitToCounterparty(selected)}
                    disabled={submitting}
                    className="bg-[#F4B340] text-[#0A0A0A] hover:bg-[#E5A220] disabled:opacity-50 px-3 py-1.5 text-[11.5px] uppercase tracking-[0.1em] font-bold rounded-sm flex items-center gap-1"
                    data-testid="audit-trail-submit-btn"
                  >
                    {submitting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                    Submit to Counterparty
                  </button>
                </div>
                {submitResult && !submitResult.error && (
                  <div className="mt-3 bg-white/10 rounded-sm p-2 text-[10.5px]" data-testid="audit-trail-submit-result">
                    <div className="font-mono opacity-80">binder: {submitResult.binder_size_bytes} bytes  ·  wire amount: ${Number(submitResult.settlement_instructions?.amount).toLocaleString()}</div>
                    {submitResult.delivery_receipts?.map((r, i) => (
                      <div key={i} className="font-mono mt-1">
                        {r.ok ? "✓" : "✗"} {r.url.slice(0, 50)} → {r.status_code || r.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Secure Payload Delivery — available for ALL deal types */}
            {currentTx && (
              <div className="bg-white border border-[#0B6B4F] rounded-sm p-4" data-testid="secure-payload-panel">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#0B6B4F] mb-1 font-bold">
                  Secure Payload Delivery
                </div>
                <div className="text-[12px] text-[#5C5C5C] mb-1 leading-snug">
                  Transmit the encrypted collateral binder + structured JSON payload (borrower identity, masked SSN, account/routing, SHA-256 hash) to any counterparty's HMAC-signed webhook — works for every deal path.
                </div>
                {renderSecurePayloadBlock(selected)}
              </div>
            )}

            {/* Market Visibility — control whether this deal shows on /market */}
            {currentTx && (
              <div className="bg-white border border-[#102968] rounded-sm p-4" data-testid="market-visibility-panel">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#102968] mb-1 font-bold">
                  Marketplace Visibility
                </div>
                <div className="text-[12px] text-[#5C5C5C] mb-2 leading-snug">
                  Control whether this deal appears on the public <span className="font-mono">/market</span> page.
                </div>
                <div className="flex gap-1.5">
                  {["public", "institutional", "hidden"].map(v => {
                    const current = currentTx.market_visibility || "public";
                    const active = current === v;
                    return (
                      <button
                        key={v}
                        onClick={async () => {
                          await axios.post(`${API}/transactions/${selected}/market-visibility`, { visibility: v });
                          await refreshList();
                        }}
                        className={`flex-1 px-2 py-1.5 text-[10.5px] uppercase tracking-[0.1em] font-bold rounded-sm transition-colors ${active ? "bg-[#102968] text-white" : "bg-white border border-[#E5E5DF] text-[#5C5C5C] hover:border-[#102968]"}`}
                        data-testid={`market-vis-${v}`}
                      >
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Stripe Checkout (Buyer pays discount price) */}
            {currentTx && (
              <div className="bg-white border border-[#635BFF] rounded-sm p-4" data-testid="audit-trail-stripe-panel">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#635BFF] mb-1 font-bold">
                  Buyer Payment Rail · Stripe
                </div>
                <div className="text-[12px] text-[#5C5C5C] mb-3 leading-snug">
                  Open a Stripe Checkout for the Buyer to pay the discount price (<span className="font-mono-fin">${Number(currentTx.purchase_price).toLocaleString()}</span>) through the platform. On success, a <span className="font-mono">buyer_payment_received</span> lifecycle event is recorded with the live Treasury benchmark.
                </div>
                <button
                  onClick={() => payWithStripe(selected)}
                  disabled={stripeLoading}
                  className="bg-[#635BFF] hover:bg-[#5046E5] text-white disabled:opacity-50 px-3 py-1.5 text-[11.5px] uppercase tracking-[0.1em] font-bold rounded-sm flex items-center gap-1"
                  data-testid="audit-trail-stripe-pay-btn"
                >
                  {stripeLoading ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={12} />}
                  Pay Discount Price with Stripe
                </button>
                {paymentStatus && (
                  <div className="mt-3 text-[10.5px] font-mono-fin" data-testid="audit-trail-stripe-status">
                    {paymentStatus.payment_status === "paid" ? (
                      <div className="text-[#0B4636] font-bold">✓ PAID · ${(paymentStatus.amount_total / 100).toLocaleString()}</div>
                    ) : paymentStatus.payment_status === "timeout" ? (
                      <div className="text-[#9E2A2B]">payment status check timed out — please refresh</div>
                    ) : (
                      <div className="text-[#5C5C5C]">status: {paymentStatus.payment_status || "—"}</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Click-to-Sign */}
            {currentTx && (
              <div className="bg-white border border-[#0B4636] rounded-sm p-4" data-testid="audit-trail-sign-panel">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#0B4636] mb-1 font-bold">
                  In-App Click-to-Sign · Closing Binder
                </div>
                <div className="text-[12px] text-[#5C5C5C] mb-3 leading-snug">
                  Capture a signer&apos;s attestation with IP, user-agent fingerprint, and SHA-256 chain-of-custody hash. Recorded as a <span className="font-mono">signed</span> lifecycle event.
                </div>

                <div className="grid grid-cols-2 gap-2 mb-2">
                  <input
                    placeholder="Signer name"
                    className="border border-[#E5E5DF] px-2 py-1 text-[12px] rounded-sm"
                    value={signerForm.name}
                    onChange={(e) => setSignerForm((f) => ({ ...f, name: e.target.value }))}
                    data-testid="audit-trail-sign-name"
                  />
                  <input
                    placeholder="signer@email.com"
                    type="email"
                    className="border border-[#E5E5DF] px-2 py-1 text-[12px] rounded-sm"
                    value={signerForm.email}
                    onChange={(e) => setSignerForm((f) => ({ ...f, email: e.target.value }))}
                    data-testid="audit-trail-sign-email"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <select
                    className="border border-[#E5E5DF] px-2 py-1 text-[12px] rounded-sm"
                    value={signerForm.role}
                    onChange={(e) => setSignerForm((f) => ({ ...f, role: e.target.value }))}
                    data-testid="audit-trail-sign-role"
                  >
                    <option>Buyer</option>
                    <option>Seller</option>
                    <option>Trustee</option>
                    <option>Counsel</option>
                    <option>Acceptance Bank</option>
                  </select>
                  <button
                    onClick={() => sign(selected)}
                    disabled={signing}
                    className="bg-[#0B4636] hover:bg-[#08362A] text-white disabled:opacity-50 px-3 py-1.5 text-[11.5px] uppercase tracking-[0.1em] font-bold rounded-sm flex items-center gap-1"
                    data-testid="audit-trail-sign-submit"
                  >
                    {signing ? <Loader2 size={12} className="animate-spin" /> : <PenTool size={12} />}
                    Sign &amp; Stamp
                  </button>
                </div>

                {signatures.length > 0 && (
                  <div className="mt-3 space-y-1.5 max-h-[120px] overflow-y-auto" data-testid="audit-trail-sign-list">
                    {signatures.map((s) => (
                      <div key={s.id} className="text-[10.5px] border-l-2 border-[#0B4636] pl-2 py-0.5">
                        <div><span className="font-bold">{s.signer_name}</span> <span className="text-[#5C5C5C]">· {s.signer_role}</span></div>
                        <div className="font-mono-fin text-[9.5px] text-[#5C5C5C]">
                          {s.signer_email} · {new Date(s.signed_at).toLocaleString()}
                        </div>
                        <div className="font-mono-fin text-[9px] text-[#8A8A8A] truncate">fp: {s.fingerprint}</div>
                      </div>
                    ))}
                  </div>
                )}

                {signatures.length > 0 && (
                  <button
                    onClick={() => downloadSignedBinder(selected)}
                    disabled={downloadingBinder}
                    className="mt-3 w-full bg-[#0B4636] hover:bg-[#08362A] text-white disabled:opacity-50 px-3 py-2 text-[11.5px] uppercase tracking-[0.1em] font-bold rounded-sm flex items-center justify-center gap-1"
                    data-testid="audit-trail-signed-binder-btn"
                  >
                    {downloadingBinder ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
                    Download Signed Binder + SHA-256
                  </button>
                )}
              </div>
            )}

            {/* Counterparty share-link (JWT-signed read-only URL) */}
            {currentTx && (
              <div className="bg-white border border-[#8B5E00] rounded-sm p-4" data-testid="audit-trail-share-panel">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#8B5E00] mb-1 font-bold">
                  Counterparty Share-Link · JWT-Signed
                </div>
                <div className="text-[12px] text-[#5C5C5C] mb-3 leading-snug">
                  Generate a read-only URL the counterparty can open without an account. Expires after 30 days. Standard bank closing-binder pattern.
                </div>
                {!shareLink ? (
                  <button
                    onClick={() => generateShareLink(selected)}
                    disabled={generatingLink}
                    className="bg-[#8B5E00] hover:bg-[#6F4900] text-white disabled:opacity-50 px-3 py-1.5 text-[11.5px] uppercase tracking-[0.1em] font-bold rounded-sm flex items-center gap-1"
                    data-testid="audit-trail-share-generate"
                  >
                    {generatingLink ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />}
                    Generate Share-Link
                  </button>
                ) : (
                  <div className="space-y-2" data-testid="audit-trail-share-link">
                    <div className="bg-[#FFF4DB] border border-[#8B5E00]/30 px-2 py-1.5 rounded-sm font-mono-fin text-[10px] break-all">
                      {shareLink.share_url}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={copyShareLink}
                        className="bg-white border border-[#8B5E00] text-[#8B5E00] hover:bg-[#FFF4DB] px-2 py-1 text-[10.5px] uppercase tracking-[0.1em] font-bold rounded-sm flex items-center gap-1"
                        data-testid="audit-trail-share-copy"
                      >
                        <Copy size={11} /> Copy
                      </button>
                      <a
                        href={shareLink.share_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white border border-[#8B5E00] text-[#8B5E00] hover:bg-[#FFF4DB] px-2 py-1 text-[10.5px] uppercase tracking-[0.1em] font-bold rounded-sm flex items-center gap-1"
                        data-testid="audit-trail-share-preview"
                      >
                        <Link2 size={11} /> Preview
                      </a>
                      <span className="text-[10px] font-mono-fin text-[#5C5C5C] self-center">
                        expires {new Date(shareLink.expires_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Bank Package ZIP (CashPro / Access / CEO / email / SFTP) */}
            {currentTx && (
              <div className="bg-white border border-[#0B4636] rounded-sm p-4" data-testid="audit-trail-bank-package-panel">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#0B4636] mb-1 font-bold">
                  Bank Submission Package · Step 1
                </div>
                <div className="text-[12px] text-[#5C5C5C] mb-3 leading-snug">
                  Generate a ZIP archive ready to upload to the discount bank&apos;s trade-finance portal. Includes a cover letter with portal-specific instructions, every legal doc as a separate PDF, the consolidated binder, and SHA-256 manifest.
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <select
                    className="border border-[#E5E5DF] px-2 py-1 text-[12px] rounded-sm"
                    value={pkgTarget}
                    onChange={(e) => setPkgTarget(e.target.value)}
                    data-testid="audit-trail-bank-package-target"
                  >
                    <option value="cashpro">Bank of America · CashPro</option>
                    <option value="jpm_access">JPMorgan · Access</option>
                    <option value="citi">Citi · CitiDirect BE</option>
                    <option value="wells_ceo">Wells Fargo · CEO</option>
                    <option value="email">Email to Trade Desk (regional bank)</option>
                    <option value="sftp">SFTP Drop Folder</option>
                  </select>
                  <button
                    onClick={() => downloadBankPackage(selected)}
                    disabled={pkgLoading}
                    className="bg-[#0B4636] hover:bg-[#08362A] text-white disabled:opacity-50 px-3 py-1.5 text-[11.5px] uppercase tracking-[0.1em] font-bold rounded-sm flex items-center gap-1"
                    data-testid="audit-trail-bank-package-btn"
                  >
                    {pkgLoading ? <Loader2 size={12} className="animate-spin" /> : <Package size={12} />}
                    Generate Bank Package ZIP
                  </button>
                </div>
              </div>
            )}

            {/* ===== Iteration 19: Real-world Settlement Integrations ===== */}
            {currentTx && (
              <div className="bg-white border border-[#102968] rounded-sm p-4 space-y-4" data-testid="audit-trail-integrations-panel">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[#102968] font-bold">
                    Settlement Integrations · Iteration 19
                  </div>
                  <div className="text-[11px] text-[#5C5C5C] mt-1 leading-snug">
                    NACHA ACH file, email delivery (Resend), e-signature (Dropbox Sign), and programmatic ACH/wire (Increase, real FDIC-insured bank). Each records a lifecycle event with the live Treasury benchmark.
                  </div>
                </div>

                {/* --- NACHA --- */}
                <div className="border-l-4 border-[#0B4636] pl-3" data-testid="integration-nacha">
                  <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#0B4636] flex items-center gap-1.5 mb-1.5">
                    <Banknote size={12} /> NACHA ACH Batch File
                  </div>
                  <div className="text-[10.5px] text-[#5C5C5C] mb-2">
                    Generate a CCD-format .txt file ready to upload to BofA CashPro / JPM Access / Wells CEO. No API keys needed.
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 mb-2">
                    <input placeholder="Seller name" className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm" value={nachaForm.seller_name} onChange={(e) => setNachaForm((f) => ({ ...f, seller_name: e.target.value }))} data-testid="nacha-seller-name" />
                    <input placeholder="Seller routing (9-digit ABA)" className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm font-mono-fin" value={nachaForm.seller_routing} onChange={(e) => setNachaForm((f) => ({ ...f, seller_routing: e.target.value }))} data-testid="nacha-seller-routing" />
                    <input placeholder="Seller account #" className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm font-mono-fin" value={nachaForm.seller_account} onChange={(e) => setNachaForm((f) => ({ ...f, seller_account: e.target.value }))} data-testid="nacha-seller-account" />
                    <input placeholder="Originator tax ID (optional)" className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm font-mono-fin" value={nachaForm.immediate_origin} onChange={(e) => setNachaForm((f) => ({ ...f, immediate_origin: e.target.value }))} data-testid="nacha-origin" />
                  </div>
                  <button
                    onClick={() => generateNacha(selected)}
                    disabled={nachaLoading}
                    className="bg-[#0B4636] hover:bg-[#08362A] text-white disabled:opacity-50 px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] font-bold rounded-sm flex items-center gap-1"
                    data-testid="nacha-generate-btn"
                  >
                    {nachaLoading ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
                    Generate &amp; Download NACHA .txt
                  </button>
                </div>

                {/* --- Resend Email --- */}
                <div className="border-l-4 border-[#F4B340] pl-3" data-testid="integration-resend">
                  <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8B5E00] flex items-center gap-1.5 mb-1.5">
                    <Mail size={12} /> Email Binder · Resend
                  </div>
                  <div className="text-[10.5px] text-[#5C5C5C] mb-2">
                    Email the Closing Binder PDF to the trade desk. Requires <span className="font-mono">RESEND_API_KEY</span> in the encrypted env panel.
                  </div>
                  <div className="flex gap-1.5 mb-1.5">
                    <input
                      type="email"
                      placeholder="trade-desk@bank.com"
                      className="flex-1 border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm"
                      value={emailTo}
                      onChange={(e) => setEmailTo(e.target.value)}
                      data-testid="resend-to-email"
                    />
                    <button
                      onClick={() => emailBinder(selected)}
                      disabled={emailLoading}
                      className="bg-[#F4B340] hover:bg-[#E5A220] text-[#0A0A0A] disabled:opacity-50 px-3 py-1 text-[11px] uppercase tracking-[0.1em] font-bold rounded-sm flex items-center gap-1"
                      data-testid="resend-send-btn"
                    >
                      {emailLoading ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                      Send
                    </button>
                  </div>
                  {emailResult && (
                    <div className="text-[10px] font-mono-fin mt-1" data-testid="resend-result">
                      {emailResult.error ? (
                        <span className="text-[#9E2A2B]">✗ {emailResult.error}</span>
                      ) : (
                        <span className="text-[#0B4636]">✓ sent · id={emailResult.id} · {emailResult.bytes}b</span>
                      )}
                    </div>
                  )}
                </div>

                {/* --- Dropbox Sign --- */}
                <div className="border-l-4 border-[#635BFF] pl-3" data-testid="integration-dropbox-sign">
                  <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#635BFF] flex items-center gap-1.5 mb-1.5">
                    <FileSignature size={12} /> E-Signature · Dropbox Sign
                  </div>
                  <div className="text-[10.5px] text-[#5C5C5C] mb-2">
                    Send the binder out for legally-binding e-signature. Requires <span className="font-mono">DROPBOX_SIGN_API_KEY</span>.
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                    <input placeholder="Signer name" className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm" value={dsForm.signer_name} onChange={(e) => setDsForm((f) => ({ ...f, signer_name: e.target.value }))} data-testid="ds-signer-name" />
                    <input placeholder="signer@email.com" type="email" className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm" value={dsForm.signer_email} onChange={(e) => setDsForm((f) => ({ ...f, signer_email: e.target.value }))} data-testid="ds-signer-email" />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-[10.5px] text-[#5C5C5C] flex items-center gap-1">
                      <input type="checkbox" checked={dsForm.test_mode} onChange={(e) => setDsForm((f) => ({ ...f, test_mode: e.target.checked }))} data-testid="ds-test-mode" />
                      test mode
                    </label>
                    <button
                      onClick={() => sendDropboxSign(selected)}
                      disabled={dsLoading}
                      className="ml-auto bg-[#635BFF] hover:bg-[#5046E5] text-white disabled:opacity-50 px-3 py-1 text-[11px] uppercase tracking-[0.1em] font-bold rounded-sm flex items-center gap-1"
                      data-testid="ds-send-btn"
                    >
                      {dsLoading ? <Loader2 size={12} className="animate-spin" /> : <FileSignature size={12} />}
                      Send for Signature
                    </button>
                  </div>
                  {dsResult && (
                    <div className="text-[10px] font-mono-fin mt-1" data-testid="ds-result">
                      {dsResult.error ? (
                        <span className="text-[#9E2A2B]">✗ {dsResult.error}</span>
                      ) : (
                        <span className="text-[#0B4636]">✓ request id={dsResult.signature_request_id} · test={String(dsResult.test_mode)}</span>
                      )}
                    </div>
                  )}
                </div>

                {/* --- Direct Exchange (bypass Increase) --- */}
                <div className="border-l-4 border-[#0B4636] pl-3" data-testid="integration-direct-exchange">
                  <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#0B4636] flex items-center gap-1.5 mb-1.5">
                    <ArrowRightLeft size={12} /> Direct Exchange · Bypass Increase
                    <span className="ml-1 text-[8.5px] bg-[#0B4636] text-white px-1.5 py-0.5 rounded-sm tracking-[0.12em]">NO API KEY</span>
                  </div>
                  <div className="text-[10.5px] text-[#5C5C5C] mb-2">
                    Skip Increase entirely. Record the counterparty&apos;s bank wire instructions; funds move directly between the buyer&apos;s bank and Treasury House&apos;s bank. Treasury House logs the settlement event for the closing binder; you mark it paid when funds arrive.
                  </div>

                  <div className="grid grid-cols-2 gap-1.5 mb-2">
                    <div>
                      <label className="text-[9.5px] uppercase tracking-[0.12em] text-[#5C5C5C] block mb-0.5">Direction</label>
                      <select
                        value={dxForm.direction}
                        onChange={(e) => setDxForm(f => ({ ...f, direction: e.target.value }))}
                        className="w-full text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white"
                        data-testid="dx-direction"
                      >
                        <option value="buyer_to_seller">Buyer → Seller (purchase price)</option>
                        <option value="seller_to_buyer">Seller → Buyer (at maturity / unwind)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[9.5px] uppercase tracking-[0.12em] text-[#5C5C5C] block mb-0.5">Rail</label>
                      <select
                        value={dxForm.rail}
                        onChange={(e) => setDxForm(f => ({ ...f, rail: e.target.value }))}
                        className="w-full text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white"
                        data-testid="dx-rail"
                      >
                        <option value="wire">Fedwire (same-day)</option>
                        <option value="ach">ACH (1-2 day)</option>
                        <option value="internal_book_transfer">Internal book transfer</option>
                      </select>
                    </div>
                  </div>

                  <input
                    type="text" placeholder="Counterparty bank name (optional)"
                    value={dxForm.counterparty_bank_name}
                    onChange={(e) => setDxForm(f => ({ ...f, counterparty_bank_name: e.target.value }))}
                    className="w-full text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 mb-1.5 bg-white font-mono-fin"
                    data-testid="dx-bank-name"
                  />
                  <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                    <input
                      type="text" placeholder="Routing #"
                      value={dxForm.counterparty_routing}
                      onChange={(e) => setDxForm(f => ({ ...f, counterparty_routing: e.target.value }))}
                      className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white font-mono-fin"
                      data-testid="dx-routing"
                    />
                    <input
                      type="text" placeholder="Account #"
                      value={dxForm.counterparty_account}
                      onChange={(e) => setDxForm(f => ({ ...f, counterparty_account: e.target.value }))}
                      className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white font-mono-fin"
                      data-testid="dx-account"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                    <input
                      type="number" placeholder={`Amount ($) · default ${Number(currentTx.purchase_price).toLocaleString()}`}
                      value={dxForm.expected_amount}
                      onChange={(e) => setDxForm(f => ({ ...f, expected_amount: e.target.value }))}
                      className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white font-mono-fin"
                      data-testid="dx-amount"
                    />
                    <input
                      type="date" placeholder="Expected settle date"
                      value={dxForm.expected_settle_date}
                      onChange={(e) => setDxForm(f => ({ ...f, expected_settle_date: e.target.value }))}
                      className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white"
                      data-testid="dx-settle-date"
                    />
                  </div>
                  <textarea
                    placeholder="Notes (optional — e.g. 'Wire from First Tennessee → Medcare operating, ref Q3 receivables')"
                    value={dxForm.notes}
                    onChange={(e) => setDxForm(f => ({ ...f, notes: e.target.value }))}
                    rows={2}
                    className="w-full text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 mb-2 bg-white"
                    data-testid="dx-notes"
                  />

                  <div className="flex gap-1.5">
                    <button
                      onClick={() => initiateDirectExchange(selected)}
                      disabled={dxLoading}
                      className="flex-1 bg-[#0B4636] hover:bg-[#0a3a2c] text-white text-[10.5px] uppercase tracking-[0.14em] py-1.5 rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
                      data-testid="dx-record-btn"
                    >
                      <ArrowRightLeft size={11} />
                      {dxLoading ? "Recording…" : "Record Direct Exchange"}
                    </button>
                  </div>

                  {/* Mark as paid (when funds arrived) */}
                  {currentTx.settlement_status === "pending_external_transfer" && (
                    <div className="mt-2 pt-2 border-t border-dashed border-[#0B4636]/40">
                      <div className="text-[10px] uppercase tracking-[0.14em] text-[#0B4636] font-bold mb-1">Funds arrived?</div>
                      <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                        <input
                          type="text" placeholder="Wire reference #"
                          value={markPaidForm.reference_number}
                          onChange={(e) => setMarkPaidForm(f => ({ ...f, reference_number: e.target.value }))}
                          className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white font-mono-fin"
                          data-testid="dx-paid-ref"
                        />
                        <input
                          type="number" placeholder="Paid amount ($)"
                          value={markPaidForm.paid_amount}
                          onChange={(e) => setMarkPaidForm(f => ({ ...f, paid_amount: e.target.value }))}
                          className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white font-mono-fin"
                          data-testid="dx-paid-amount"
                        />
                      </div>
                      <button
                        onClick={() => markSettlementPaid(selected)}
                        disabled={dxLoading}
                        className="w-full bg-[#1a3a8e] hover:bg-[#142c6c] text-white text-[10.5px] uppercase tracking-[0.14em] py-1.5 rounded-sm disabled:opacity-50"
                        data-testid="dx-mark-paid-btn"
                      >
                        ✓ Mark Paid · Close Deal
                      </button>
                    </div>
                  )}

                  {dxResult && (
                    <div className={`mt-2 p-2 rounded-sm text-[10.5px] ${dxResult.error ? "bg-red-50 text-red-800 border border-red-200" : dxResult._paid ? "bg-blue-50 text-[#1a3a8e] border border-blue-200" : "bg-[#0B4636]/10 text-[#0B4636] border border-[#0B4636]/30"}`}>
                      {dxResult.error ? (
                        <>Error: {dxResult.error}</>
                      ) : dxResult._paid ? (
                        <>
                          <div className="font-bold">✓ Settlement marked PAID</div>
                          <div className="font-mono-fin text-[10px] mt-0.5">Ref: {dxResult.reference_number || "—"} · ${Number(dxResult.paid_amount_usd).toLocaleString()} · {dxResult.paid_at?.slice(0, 19)}</div>
                        </>
                      ) : (
                        <>
                          <div className="font-bold">✓ Direct exchange recorded</div>
                          <div className="text-[10px] mt-0.5">Status: pending_external_transfer · Amount: ${Number(dxResult.expected_amount_usd).toLocaleString()}</div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* --- Direct to Creditor (debt settlement / swap / collateral) --- */}
                <div className="border-l-4 border-[#8B5E00] pl-3" data-testid="integration-direct-creditor">
                  <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8B5E00] flex items-center gap-1.5 mb-1.5">
                    <Building2 size={12} /> Direct to Creditor · Debt Resolution
                  </div>
                  <div className="text-[10.5px] text-[#5C5C5C] mb-2">
                    Sell, transfer or tender this note package to a lender/creditor to settle, swap, finance, refinance or
                    collateralize an existing debt obligation. Generates the matching instruments into the binder, then
                    delivers a secure link to the creditor.
                  </div>

                  <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                    <div>
                      <label className="text-[9.5px] uppercase tracking-[0.12em] text-[#5C5C5C] block mb-0.5">Purpose</label>
                      <select value={crForm.purpose} onChange={(e) => setCrForm(f => ({ ...f, purpose: e.target.value }))}
                        className="w-full text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white" data-testid="cr-purpose">
                        <option value="settle_obligation">Settle Existing Obligation</option>
                        <option value="debt_settlement">Debt Settlement</option>
                        <option value="debt_swap">Debt Swap</option>
                        <option value="collateral">Pledge as Collateral</option>
                        <option value="finance">Finance</option>
                        <option value="refinance">Refinance</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[9.5px] uppercase tracking-[0.12em] text-[#5C5C5C] block mb-0.5">Pre-loaded debt</label>
                      <select onChange={(e) => applyDebtTemplate(e.target.value)} defaultValue=""
                        className="w-full text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white" data-testid="cr-template">
                        <option value="">— select / TBD —</option>
                        {debtTemplates.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                    <input type="text" placeholder="Creditor / lender name" value={crForm.creditor_name}
                      onChange={(e) => setCrForm(f => ({ ...f, creditor_name: e.target.value }))}
                      className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white" data-testid="cr-name" />
                    <input type="email" placeholder="Creditor email (for delivery)" value={crForm.creditor_email}
                      onChange={(e) => setCrForm(f => ({ ...f, creditor_email: e.target.value }))}
                      className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white" data-testid="cr-email" />
                  </div>
                  <input type="text" placeholder="Creditor address (optional)" value={crForm.creditor_address}
                    onChange={(e) => setCrForm(f => ({ ...f, creditor_address: e.target.value }))}
                    className="w-full text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white mb-1.5" data-testid="cr-address" />

                  <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                    <div>
                      <label className="text-[9.5px] uppercase tracking-[0.12em] text-[#5C5C5C] block mb-0.5">Debt type</label>
                      <select value={crForm.debt_type} onChange={(e) => setCrForm(f => ({ ...f, debt_type: e.target.value }))}
                        className="w-full text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white" data-testid="cr-debt-type">
                        <option value="mortgage_sf">Mortgage — Single Family</option>
                        <option value="mortgage_mf">Mortgage — Multifamily</option>
                        <option value="mortgage_commercial">Mortgage — Commercial</option>
                        <option value="auto">Auto Loan</option>
                        <option value="student">Student Loan</option>
                        <option value="loc">Line of Credit</option>
                        <option value="credit_card">Credit Card</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <input type="text" placeholder="Account / loan #" value={crForm.account_number}
                      onChange={(e) => setCrForm(f => ({ ...f, account_number: e.target.value }))}
                      className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white self-end font-mono-fin" data-testid="cr-account" />
                  </div>

                  <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                    <input type="number" placeholder="Current balance ($)" value={crForm.current_balance}
                      onChange={(e) => setCrForm(f => ({ ...f, current_balance: e.target.value }))}
                      className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white font-mono-fin" data-testid="cr-balance" />
                    <input type="number" placeholder="Payoff / settle amount ($)" value={crForm.payoff_amount}
                      onChange={(e) => setCrForm(f => ({ ...f, payoff_amount: e.target.value }))}
                      className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white font-mono-fin" data-testid="cr-payoff" />
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 mb-2">
                    <input type="text" placeholder="Servicer (optional)" value={crForm.servicer}
                      onChange={(e) => setCrForm(f => ({ ...f, servicer: e.target.value }))}
                      className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white" data-testid="cr-servicer" />
                    <input type="date" value={crForm.effective_date}
                      onChange={(e) => setCrForm(f => ({ ...f, effective_date: e.target.value }))}
                      className="text-[11px] border border-[#E5E5DF] rounded-sm px-2 py-1 bg-white" data-testid="cr-effective-date" />
                  </div>

                  <div className="flex gap-1.5">
                    <button onClick={() => submitCreditorDirection(selected)} disabled={crLoading}
                      className="flex-1 bg-[#8B5E00] hover:bg-[#714c00] text-white text-[10.5px] uppercase tracking-[0.14em] py-1.5 rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
                      data-testid="cr-generate-btn">
                      <Building2 size={11} /> {crLoading ? "Generating…" : "Generate Instruments"}
                    </button>
                    <button onClick={() => deliverToCreditor(selected)} disabled={crDeliverLoading}
                      className="flex-1 border border-[#8B5E00] text-[#8B5E00] hover:bg-[#8B5E00]/10 text-[10.5px] uppercase tracking-[0.14em] py-1.5 rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
                      data-testid="cr-deliver-btn">
                      {crDeliverLoading ? "Sending…" : "Deliver to Creditor"}
                    </button>
                  </div>

                  {crResult && (
                    <div className={`mt-2 p-2 rounded-sm text-[10.5px] ${crResult.error ? "bg-red-50 text-red-800 border border-red-200" : "bg-[#8B5E00]/10 text-[#8B5E00] border border-[#8B5E00]/30"}`} data-testid="cr-result">
                      {crResult.error ? (<>Error: {crResult.error}</>)
                        : crResult.delivered ? (<><div className="font-bold">✓ Secure package link sent</div><div className="text-[10px] mt-0.5">to {crResult.to}</div></>)
                        : (<><div className="font-bold">✓ Instruments generated into binder</div><div className="text-[10px] mt-0.5">{(crResult.instruments || []).length} document(s) added · open the binder to review.</div></>)}
                    </div>
                  )}
                </div>

                {/* --- Increase Programmatic ACH / Wire --- */}
                <div className="border-l-4 border-[#9E2A2B] pl-3" data-testid="integration-increase">
                  <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#9E2A2B] flex items-center gap-1.5 mb-1.5">
                    <Building2 size={12} /> Programmatic ACH/Wire · Increase
                    <span className="ml-1 text-[8.5px] bg-[#9E2A2B] text-white px-1.5 py-0.5 rounded-sm tracking-[0.12em]">LIVE</span>
                  </div>
                  <div className="text-[10.5px] text-[#5C5C5C] mb-2">
                    Initiate a real ACH or wire from your Increase production account for the discount price (<span className="font-mono-fin">${Number(currentTx.purchase_price).toLocaleString()}</span>). Requires approval in Increase dashboard by default.
                  </div>

                  {/* Source account picker */}
                  <div className="mb-1.5">
                    <label className="text-[9.5px] uppercase tracking-[0.12em] text-[#5C5C5C] block mb-0.5">Source Increase Account</label>
                    {incAccounts === null ? (
                      <div className="text-[10.5px] text-[#8A8A8A]"><Loader2 size={11} className="animate-spin inline mr-1" /> loading accounts…</div>
                    ) : incAccounts?.error ? (
                      <div className="text-[10.5px] text-[#9E2A2B] font-mono-fin">✗ {incAccounts.error}</div>
                    ) : incAccounts.length === 0 ? (
                      <div className="text-[10.5px] text-[#8B5E00] bg-[#FFF4DB] border border-[#8B5E00]/30 px-2 py-1.5 rounded-sm">
                        No Increase accounts found on this API key. Go to <a href="https://dashboard.increase.com" target="_blank" rel="noreferrer" className="underline">dashboard.increase.com → Accounts</a> and provision an account first.
                      </div>
                    ) : (
                      <select
                        className="w-full border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm font-mono-fin"
                        value={incForm.account_id}
                        onChange={(e) => setIncForm((f) => ({ ...f, account_id: e.target.value }))}
                        data-testid="inc-account-id"
                      >
                        {incAccounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name || "(unnamed)"} · {a.id} · {a.status}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                    <select
                      className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm"
                      value={incForm.rail}
                      onChange={(e) => setIncForm((f) => ({ ...f, rail: e.target.value }))}
                      data-testid="inc-rail"
                    >
                      <option value="ach">ACH (next-day)</option>
                      <option value="wire">Wire (same-day)</option>
                    </select>
                    <input placeholder="Beneficiary name" className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm" value={incForm.beneficiary_name} onChange={(e) => setIncForm((f) => ({ ...f, beneficiary_name: e.target.value }))} data-testid="inc-beneficiary" />
                    <input placeholder="Routing # (9-digit)" className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm font-mono-fin" value={incForm.routing_number} onChange={(e) => setIncForm((f) => ({ ...f, routing_number: e.target.value }))} data-testid="inc-routing" />
                    <input placeholder="Account #" className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm font-mono-fin" value={incForm.account_number} onChange={(e) => setIncForm((f) => ({ ...f, account_number: e.target.value }))} data-testid="inc-account" />
                  </div>
                  <button
                    onClick={() => initiateIncreaseTransfer(selected)}
                    disabled={incLoading || !incAccounts || incAccounts.error || incAccounts.length === 0}
                    className="bg-[#9E2A2B] hover:bg-[#7a1f20] text-white disabled:opacity-50 px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] font-bold rounded-sm flex items-center gap-1"
                    data-testid="inc-transfer-btn"
                  >
                    {incLoading ? <Loader2 size={12} className="animate-spin" /> : <Building2 size={12} />}
                    Initiate {incForm.rail.toUpperCase()} for ${Number(currentTx.purchase_price).toLocaleString()}
                  </button>
                  {incResult && (
                    <div className="text-[10px] font-mono-fin mt-1.5" data-testid="inc-result">
                      {incResult.error ? (
                        <span className="text-[#9E2A2B]">✗ {incResult.error}</span>
                      ) : (
                        <span className="text-[#0B4636]">✓ {incResult.id} · {incResult.status} · ${(incResult.amount_cents / 100).toLocaleString()} → ****{incResult.account_last4}</span>
                      )}
                    </div>
                  )}
                </div>

                {/* --- Inbound ACH / Wire — receive payments INTO Increase --- */}
                <div className="border-l-4 border-[#0B4636] pl-3" data-testid="integration-inbound-ach">
                  <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#0B4636] flex items-center gap-1.5 mb-1.5">
                    <Banknote size={12} /> Receive Payment · Inbound ACH/Wire
                  </div>
                  <div className="text-[10.5px] text-[#5C5C5C] mb-2">
                    Share these credentials with a buyer to receive an ACH credit, wire, or RTP push into your Increase account for this deal.
                  </div>
                  <button
                    onClick={fetchDepositInstructions}
                    disabled={depositLoading || !incForm.account_id}
                    className="bg-[#0B4636] hover:bg-[#08362A] text-white disabled:opacity-50 px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] font-bold rounded-sm flex items-center gap-1"
                    data-testid="get-deposit-instructions-btn"
                  >
                    {depositLoading ? <Loader2 size={12} className="animate-spin" /> : <Banknote size={12} />}
                    Get Deposit Instructions
                  </button>
                  {depositInfo && !depositInfo.error && (
                    <div className="mt-2 bg-[#F5F5F0] border border-[#0B4636] rounded-sm p-3 space-y-1.5 font-mono-fin text-[11px]" data-testid="deposit-info-panel">
                      {depositInfo.needs_manual_provisioning ? (
                        <div className="text-[10.5px] text-[#8B5E00] bg-[#FFF4DB] border border-[#8B5E00]/30 px-2 py-1.5 rounded-sm font-sans">
                          <div className="font-bold uppercase tracking-[0.1em] text-[10px] mb-1">No Account Number active</div>
                          <div>{depositInfo.manual_provisioning_instructions}</div>
                          {depositInfo.provision_error && (
                            <div className="text-[9.5px] mt-1 opacity-70">Auto-provision attempt: {depositInfo.provision_error.slice(0, 120)}</div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-[#5C5C5C] uppercase tracking-[0.1em] text-[9.5px]">Bank Name</span>
                            <span className="text-[#0A0A0A]">{depositInfo.bank_name}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-[#5C5C5C] uppercase tracking-[0.1em] text-[9.5px]">Account Holder</span>
                            <span className="text-[#0A0A0A] font-bold">{depositInfo.account_name}</span>
                          </div>
                          <div className="flex justify-between items-center group cursor-pointer" onClick={() => copyToClipboard(depositInfo.routing_number, "Routing number")}>
                            <span className="text-[#5C5C5C] uppercase tracking-[0.1em] text-[9.5px]">Routing # (ABA)</span>
                            <span className="text-[#0A0A0A] group-hover:underline" data-testid="deposit-routing">{depositInfo.routing_number} <Copy size={9} className="inline opacity-50" /></span>
                          </div>
                          <div className="flex justify-between items-center group cursor-pointer" onClick={() => copyToClipboard(depositInfo.account_number, "Account number")}>
                            <span className="text-[#5C5C5C] uppercase tracking-[0.1em] text-[9.5px]">Account #</span>
                            <span className="text-[#0A0A0A] group-hover:underline" data-testid="deposit-account">{depositInfo.account_number} <Copy size={9} className="inline opacity-50" /></span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-[#5C5C5C] uppercase tracking-[0.1em] text-[9.5px]">Account Type</span>
                            <span className="text-[#0A0A0A]">Checking · USD</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-[#5C5C5C] uppercase tracking-[0.1em] text-[9.5px]">Reference</span>
                            <span className="text-[#0A0A0A]">{currentTx?.trust_name || currentTx?.id?.slice(0,8)}</span>
                          </div>
                          <div className="text-[9.5px] text-[#5C5C5C] pt-1.5 border-t border-[#D1D1C7] mt-2">
                            Tap any field to copy. Funds typically arrive next-business-day for ACH, same-day for wires &amp; RTP.
                          </div>
                          <a
                            href={`${API}/transactions/${selected}/wire-instructions.pdf?account_id=${incForm.account_id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 inline-flex items-center gap-1 bg-white hover:bg-[#F0F4FA] border border-[#0B4636] text-[#0B4636] px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] font-bold rounded-sm"
                            data-testid="download-wire-instructions-pdf"
                          >
                            <FileDown size={11} /> Download Wire Instructions PDF
                          </a>
                        </>
                      )}
                    </div>
                  )}
                  {depositInfo?.error && (
                    <div className="text-[10px] text-[#9E2A2B] mt-1 font-mono-fin">✗ {depositInfo.error}</div>
                  )}
                </div>

                {/* --- Counterparty Library — saved bank/buyer profiles --- */}
                <div className="border-l-4 border-[#102968] pl-3" data-testid="integration-counterparty-library">
                  <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#102968] flex items-center gap-1.5 mb-1.5">
                    <FileSignature size={12} /> Counterparty Library
                  </div>
                  <div className="text-[10.5px] text-[#5C5C5C] mb-2">
                    Saved bank/buyer profiles. Click to auto-fill beneficiary, routing/account, and signer email across all panels above.
                  </div>
                  {cps.length > 0 ? (
                    <div className="space-y-1 mb-2 max-h-[140px] overflow-y-auto">
                      {cps.map(cp => (
                        <button
                          key={cp.id}
                          onClick={() => applyCounterparty(cp)}
                          className="w-full text-left bg-white hover:bg-[#F0F4FA] border border-[#E5E5DF] rounded-sm px-2 py-1.5 transition-colors"
                          data-testid={`cp-apply-${cp.id}`}
                        >
                          <div className="text-[11.5px] font-semibold text-[#0A0A0A]">{cp.name}</div>
                          <div className="text-[9.5px] text-[#5C5C5C] uppercase tracking-[0.1em] font-mono-fin">
                            {cp.role} {cp.routing_number ? `· ${cp.routing_number}` : ""} {cp.contact_email ? `· ${cp.contact_email}` : ""}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-[10.5px] text-[#8A8A8A] italic mb-2">No saved profiles yet. Save one below to autocomplete future deals.</div>
                  )}
                  {!showSaveCp ? (
                    <button
                      onClick={() => setShowSaveCp(true)}
                      className="text-[11px] uppercase tracking-[0.1em] font-bold text-[#102968] hover:underline"
                      data-testid="cp-show-save-btn"
                    >
                      + Save current counterparty
                    </button>
                  ) : (
                    <div className="bg-white border border-[#102968] rounded-sm p-2 space-y-1.5">
                      <div className="grid grid-cols-2 gap-1.5">
                        <input placeholder="Profile name *" className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm" value={cpForm.name} onChange={e => setCpForm(f => ({ ...f, name: e.target.value }))} data-testid="cp-name" />
                        <select className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm" value={cpForm.role} onChange={e => setCpForm(f => ({ ...f, role: e.target.value }))} data-testid="cp-role">
                          <option value="buyer">Buyer</option>
                          <option value="lender">Lender</option>
                          <option value="dealer">Dealer</option>
                          <option value="bank">Bank</option>
                          <option value="trustee">Trustee</option>
                          <option value="other">Other</option>
                        </select>
                        <input placeholder="Legal name (optional)" className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm" value={cpForm.legal_name} onChange={e => setCpForm(f => ({ ...f, legal_name: e.target.value }))} />
                        <input placeholder="Bank name" className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm" value={cpForm.bank_name} onChange={e => setCpForm(f => ({ ...f, bank_name: e.target.value }))} />
                        <input placeholder="Routing #" className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm font-mono-fin" value={cpForm.routing_number} onChange={e => setCpForm(f => ({ ...f, routing_number: e.target.value }))} />
                        <input placeholder="Account #" className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm font-mono-fin" value={cpForm.account_number} onChange={e => setCpForm(f => ({ ...f, account_number: e.target.value }))} />
                        <input placeholder="Contact name" className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm" value={cpForm.contact_name} onChange={e => setCpForm(f => ({ ...f, contact_name: e.target.value }))} />
                        <input placeholder="Contact email" type="email" className="border border-[#E5E5DF] px-2 py-1 text-[11.5px] rounded-sm" value={cpForm.contact_email} onChange={e => setCpForm(f => ({ ...f, contact_email: e.target.value }))} />
                      </div>
                      <div className="flex gap-1.5">
                        <button onClick={saveCounterparty} className="flex-1 bg-[#102968] hover:bg-[#0a1f50] text-white px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] font-bold rounded-sm" data-testid="cp-save-btn">
                          Save Profile
                        </button>
                        <button onClick={() => setShowSaveCp(false)} className="px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[#5C5C5C] hover:text-[#0A0A0A]">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Benchmark drift summary */}
            {replay?.benchmark_drift && (
              <div className="bg-white border border-[#102968] rounded-sm p-4" data-testid="audit-trail-drift">
                <div className="text-[10px] font-mono-fin uppercase tracking-[0.16em] text-[#102968]">
                  Treasury Benchmark Drift
                </div>
                <div className="flex items-baseline gap-3 mt-2">
                  <span className="font-mono-fin text-[#5C5C5C] text-[13px]">{fmtPct(replay.benchmark_drift.start_rate)}</span>
                  <span className="text-[#8A8A8A]">→</span>
                  <span className="font-mono-fin text-[#102968] text-[15px] font-semibold">{fmtPct(replay.benchmark_drift.end_rate)}</span>
                  <span className={`ml-auto text-[12px] font-mono-fin font-semibold ${replay.benchmark_drift.delta_bps >= 0 ? "text-[#9E2A2B]" : "text-[#0B4636]"}`}>
                    {replay.benchmark_drift.delta_bps >= 0 ? "+" : ""}{replay.benchmark_drift.delta_bps} bps
                  </span>
                </div>
              </div>
            )}

            {/* Lifecycle advance actions */}
            {currentTx && currentTx.status !== "matured" && (
              <div className="bg-white border border-[#E5E5DF] rounded-sm p-3" data-testid="audit-trail-advance">
                <div className="text-[10px] font-mono-fin uppercase tracking-[0.16em] text-[#5C5C5C] mb-2">Advance Lifecycle</div>
                <div className="flex flex-wrap gap-2">
                  {["trust_formed", "note_issued", "ba_accepted", "sale_executed", "proceeds_disbursed", "maturity_paid"].map((t) => (
                    <button
                      key={t}
                      disabled={advancing === t}
                      onClick={() => advance(selected, t)}
                      className="text-[11px] border border-[#E5E5DF] hover:border-[#0B4636] hover:text-[#0B4636] px-2 py-1 rounded-sm font-mono-fin disabled:opacity-50"
                      data-testid={`audit-trail-advance-${t}`}
                    >
                      {advancing === t ? <Loader2 size={11} className="animate-spin inline mr-1" /> : null}
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Timeline */}
            <div data-testid="audit-trail-timeline">
              <div className="text-[10px] font-mono-fin uppercase tracking-[0.16em] text-[#5C5C5C] mb-2">
                Timeline · {replay?.count || 0} events
              </div>
              {!replay && (
                <div className="text-center py-6 text-[#8A8A8A] text-[12px]">
                  <Loader2 size={14} className="animate-spin inline mr-2" /> Loading replay…
                </div>
              )}
              {replay?.timeline?.map((ev, idx) => (
                <div
                  key={idx}
                  className={`border-l-4 ${KIND_COLORS[ev.kind] || "border-l-[#E5E5DF] bg-white"} px-3 py-2 mb-2 rounded-sm`}
                  data-testid={`audit-trail-event-${idx}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono-fin text-[12px] font-semibold">{ev.type}</div>
                    <div className="text-[10px] font-mono-fin text-[#8A8A8A]">{fmtTs(ev.ts)}</div>
                  </div>
                  {ev.kind === "lifecycle" && ev.treasury_benchmark?.avg_interest_rate && (
                    <div className="text-[10.5px] text-[#5C5C5C] mt-0.5">
                      Stamped at Treasury benchmark <span className="font-mono-fin text-[#0B4636]">{Number(ev.treasury_benchmark.avg_interest_rate).toFixed(3)}%</span> ({ev.treasury_benchmark.record_date})
                    </div>
                  )}
                  {ev.kind === "treasury_delta" && (
                    <div className="text-[10.5px] text-[#5C5C5C] mt-0.5 font-mono-fin">
                      benchmark changed during trade
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Footer actions */}
            <div className="border-t border-[#E5E5DF] pt-3 flex justify-end">
              <button
                className="text-[#9E2A2B] hover:text-[#7a1f20] flex items-center gap-1 text-[12px]"
                onClick={() => deleteTx(selected)}
                data-testid="audit-trail-delete-btn"
              >
                <Trash2 size={13} /> Delete transaction
              </button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
